// src/modules/ida/components/FullPageChatLayout.tsx
//
// Full-page chat layout for IDA using mmagent-chat-ui components.

import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Divider,
  Input,
  Textarea,
  Button,
  CircularProgress,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Avatar,
  Chip,
  FormControl,
  FormLabel,
  Checkbox,
  Snackbar,
  Menu,
  MenuItem,
  Select,
  Option,
} from "@mui/joy";
import Popper from "@mui/material/Popper";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import StopIcon from "@mui/icons-material/Stop";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ChatMessage,
  AttachmentPicker,
  JobProgressIndicator,
  useChatStore,
} from "@metalmind-ab/mmagent-chat-ui";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import { FilePreviewChips, ALLOWED_EXTENSIONS } from "./FilePreviewChips";
import { ToolComposingIndicator } from "./ToolComposingIndicator";
import { VoiceInputButton } from "./voice/VoiceInputButton";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type { User, WorkspaceUser } from "../../../common/types";
import {
  useDownloadDocument,
  useResolveDocumentDownloadLink,
} from "../services/api";
import type { ChatMessageWithFiles } from "../hooks/useSendMessageWithFiles";
import type { SkillInvocationAttribution } from "../../../common/types";
import type {
  ChatMessageType,
  ContentRenderer,
  Citation,
  ChatIcons,
  ChatMessageExtensions,
  ChatToolExecutionExtensions,
  ToolExecution,
  ImageReference,
  SelectedImage,
  SelectedAttachment,
} from "@metalmind-ab/mmagent-chat-ui";
import { UsageInfoBadge, MessageUsage } from "./UsageInfoBadge";
import type { ConversationQueueItem } from "../hooks/useConversationQueue";

import HeartbeatTriggerCard from "./HeartbeatTriggerCard";
import FollowUpSuggestionChips from "./FollowUpSuggestionChips";
import {
  getHeartbeatMetadata,
  INTERRUPTED_NOTICE_TEXT,
  STOPPED_BY_USER_NOTICE_TEXT,
  type IDAChatMessage,
} from "../utils/chatMessageMapper";
import {
  findDuplicateMessageKeyDiagnostics,
  serializeDuplicateMessageKeyDiagnostic,
} from "../utils/messageListDiagnostics";
import { extractMessageUsage } from "../utils/messageUsage";
import type {
  CollaborationConversationSummary,
  AgentAvatarReservations,
  ReusablePersona,
  ConversationCollaborator,
  ReusablePersonaGroups,
} from "../services/collaboration";
import { canShowAgentActionMenu } from "../services/agentPermissions";
import { usePersonaLibrary } from "../services/collaboration";
import {
  buildSharedConversationLabel,
  getConversationById,
  getUserInitials,
  resolveSpeakerDisplayInfo,
} from "../utils/collaboration";
import SkillInvocationAttributionCard from "./SkillInvocationAttributionCard";
import LlmRouteProvenanceChip from "./LlmRouteProvenanceChip";
import { resolveDisplayedAttributions } from "./skillInvocationAttributionState";
import CollaboratorManagementModal from "./CollaboratorManagementModal";
import { toInternalDownloadLinkEndpoint } from "../utils/internalDownloadLinks";
import { AgentGemstone, getAgentGemstoneBubbleTone } from "./AgentGemstone";
import { resolveAgentAvatarDisplay } from "./agentAvatarIdentity";
import BackgroundDocumentCompletion from "./BackgroundDocumentCompletion";
import { nonEmptyDocumentString } from "../utils/documentIdentity";
import { DOCUMENT_JOB_CHECK_BACK_MESSAGE } from "../utils/backgroundJobTerminal";
import { subscribeToConversationSseFrames } from "../utils/idaChatStreamConfig";
import {
  getDocumentJobForExecution,
  recordDocumentJobBinding,
} from "../utils/documentJobBindings";
import {
  AGENT_AVATAR_COLOURS,
  AGENT_AVATAR_CUTS,
  type AgentAvatarColour,
  type AgentAvatarCut,
} from "./agentAvatarIdentity";
import {
  resolveAgentActivityDisplay,
  useAgentActivityClock,
} from "../utils/agentActivityDisplay";

export const IDA_HISTORY_PILL_KEY = "ida_history_pill_count";
const HISTORY_PILL_MAX_SHOWS = 3;

// Evaluated once at module load so React StrictMode's double-fired effects
// don't increment the counter twice or cancel the collapse timer mid-cycle.
const shouldShowHistoryPill = (() => {
  try {
    const count = parseInt(
      localStorage.getItem(IDA_HISTORY_PILL_KEY) ?? "0",
      10,
    );
    if (count < HISTORY_PILL_MAX_SHOWS) {
      localStorage.setItem(IDA_HISTORY_PILL_KEY, String(count + 1));
      return true;
    }
  } catch {}
  return false;
})();

// How close to the bottom still counts as "following the conversation".
// Generous enough to survive a partially-rendered final line.
const SCROLL_PIN_THRESHOLD_PX = 80;

// How long after a reader's gesture a resulting scroll still counts as theirs.
// Long enough to cover momentum scrolling, short enough that it cannot be
// mistaken for a later automatic scroll.
const HUMAN_SCROLL_INTENT_WINDOW_MS = 1500;

const AGENT_SELECTED_BORDER = "#f0b429";
const AGENT_TEXT_SHADOW = "0 1px 2px rgba(0,0,0,0.3)";

function buildAgentOpenAriaLabel(displayName: string): string {
  return `Open ${displayName.trim()}`;
}

function usePointerCoarse(): boolean {
  const [isCoarse, setIsCoarse] = React.useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const update = () => setIsCoarse(mediaQuery.matches);
    update();
    mediaQuery.addEventListener?.("change", update);
    return () => mediaQuery.removeEventListener?.("change", update);
  }, []);

  return isCoarse;
}

interface AgentInventoryTileProps {
  conversation: CollaborationConversationSummary;
  canAccessActions: boolean;
  displayName: string;
  selected: boolean;
  isMenuOpen: boolean;
  isCoarsePointer: boolean;
  disabled: boolean;
  activityNow: number;
  onSelect: (id: number) => void;
  onOpenMenu: (
    event: React.SyntheticEvent<HTMLElement>,
    conversation: CollaborationConversationSummary,
  ) => void;
}

const AgentInventoryTile = React.memo(
  ({
    conversation,
    canAccessActions,
    displayName,
    selected,
    isMenuOpen,
    isCoarsePointer,
    disabled,
    activityNow,
    onSelect,
    onOpenMenu,
  }: AgentInventoryTileProps) => {
    const [nameAnchor, setNameAnchor] = React.useState<HTMLElement | null>(
      null,
    );
    const hoverDelayRef = React.useRef<number | null>(null);
    // Keep inventory geometry independent of the avatar's recovery state.
    // The old full recovery sentence was absolutely positioned in a 56px
    // tile, so a populated inventory painted it over neighboring stones.
    const tileWidth = isCoarsePointer ? 76 : 64;
    const avatarSize = isCoarsePointer ? 42 : 38;
    const tileHeight = isCoarsePointer ? 70 : 62;
    const initialsSize = 16;
    const initialsGap = 4;
    const activity = resolveAgentActivityDisplay(
      conversation.agentStatus,
      conversation.agentLastHeartbeatAt,
      activityNow,
    );

    const clearHoverDelay = useCallback(() => {
      if (hoverDelayRef.current !== null) {
        window.clearTimeout(hoverDelayRef.current);
        hoverDelayRef.current = null;
      }
    }, []);

    useEffect(() => clearHoverDelay, [clearHoverDelay]);

    const handleMouseEnter = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        clearHoverDelay();
        const anchor = event.currentTarget;
        hoverDelayRef.current = window.setTimeout(() => {
          setNameAnchor(anchor);
          hoverDelayRef.current = null;
        }, 80);
      },
      [clearHoverDelay],
    );

    const handleMouseLeave = useCallback(() => {
      clearHoverDelay();
      setNameAnchor(null);
    }, [clearHoverDelay]);

    const handleSelect = useCallback(() => {
      if (disabled) return;
      onSelect(conversation.id);
    }, [conversation.id, disabled, onSelect]);

    const handleOpenMenu = useCallback(
      (event: React.SyntheticEvent<HTMLElement>) => {
        if (disabled) return;
        onOpenMenu(event, conversation);
      },
      [conversation, disabled, onOpenMenu],
    );

    return (
      <Box
        data-testid={`agent-inventory-tile-${conversation.id}`}
        data-tile-width={tileWidth}
        data-tile-height={tileHeight}
        data-avatar-size={avatarSize}
        data-initials-size={initialsSize}
        data-initials-gap={initialsGap}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          flex: "0 0 auto",
          width: tileWidth,
          minWidth: tileWidth,
          height: tileHeight,
          px: 0.5,
          pt: 0.5,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 0.25,
          "& .agent-menu-trigger": {
            opacity: isMenuOpen ? 1 : 0,
            pointerEvents: isMenuOpen ? "auto" : "none",
            transition: "opacity 120ms ease",
          },
          "&:hover .agent-menu-trigger, &:focus-within .agent-menu-trigger": {
            opacity: 1,
            pointerEvents: "auto",
          },
        }}
      >
        <IconButton
          size="sm"
          variant="solid"
          onClick={handleSelect}
          disabled={disabled}
          aria-label={buildAgentOpenAriaLabel(displayName)}
          data-testid={`agent-inventory-agent-${conversation.id}`}
          sx={{
            width: avatarSize,
            height: avatarSize,
            minWidth: avatarSize,
            minHeight: avatarSize,
            flex: "0 0 auto",
            overflow: "visible",
            borderRadius: "50%",
            bgcolor: "transparent",
            color: "inherit",
            border: selected
              ? `2px solid ${AGENT_SELECTED_BORDER}`
              : "2px solid transparent",
            textShadow: AGENT_TEXT_SHADOW,
            boxShadow: selected ? "0 0 0 1px rgba(240, 180, 41, 0.25)" : "none",
            "&:hover": {
              bgcolor: "transparent",
            },
          }}
        >
          <AgentGemstone
            id={conversation.id}
            cut={conversation.avatar?.cut}
            colour={conversation.avatar?.colour}
            state={
              conversation.agentStatus === "generating" ||
              conversation.agentStatus === "running_tool"
                ? "working"
                : "idle"
            }
            agentStatus={conversation.agentStatus}
            agentLastHeartbeatAt={conversation.agentLastHeartbeatAt}
            activity={activity}
            size={avatarSize}
            title={`${displayName} appearance`}
            testId={`agent-gemstone-${conversation.id}`}
            showStatusCircle
          />
        </IconButton>
        <Typography
          data-testid={`agent-inventory-initials-${conversation.id}`}
          aria-label={`${displayName} initials`}
          level="body-xs"
          sx={{
            minWidth: initialsSize,
            height: initialsSize,
            flex: "0 0 auto",
            px: 0.35,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            bgcolor: selected ? "primary.600" : "neutral.700",
            color: "common.white",
            border: "1px solid",
            borderColor: "background.surface",
            fontSize: 9,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: "0.02em",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          {getUserInitials(displayName)}
        </Typography>
        {canAccessActions && (
          <IconButton
            className="agent-menu-trigger"
            size="sm"
            variant="solid"
            color="neutral"
            onClick={handleOpenMenu}
            disabled={disabled}
            aria-label={`${displayName} actions`}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen ? true : undefined}
            data-testid={`agent-inventory-agent-${conversation.id}-menu-trigger`}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              width: 20,
              height: 20,
              minWidth: 20,
              minHeight: 20,
              borderRadius: "50%",
              bgcolor: "rgba(17,24,39,0.82)",
              color: "#fff",
              boxShadow: "sm",
              "--IconButton-size": "20px",
              "&:hover": {
                bgcolor: "rgba(17,24,39,0.92)",
              },
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <Popper
          open={Boolean(nameAnchor) && !isMenuOpen}
          anchorEl={nameAnchor}
          placement="top-start"
          modifiers={[
            { name: "offset", options: { offset: [0, 4] } },
            // Task #577: agent-inventory-bar sits directly below the page banner,
            // so the default "top-start" tooltip rendered ON TOP of the SSI brand
            // logo. Flip to a below-tile placement when there's no room above,
            // and keep the popper inside the viewport so it never escapes.
            {
              name: "flip",
              options: {
                fallbackPlacements: ["bottom-start", "bottom", "bottom-end"],
              },
            },
            { name: "preventOverflow", options: { padding: 8 } },
          ]}
          sx={{ zIndex: 1300 }}
        >
          <Box
            aria-hidden="true"
            data-testid={`agent-inventory-agent-${conversation.id}-popover`}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: "sm",
              bgcolor: "background.popup",
              color: "text.primary",
              boxShadow: "md",
              border: "1px solid",
              borderColor: "divider",
              fontSize: "0.75rem",
              maxWidth: 220,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName}
          </Box>
        </Popper>
      </Box>
    );
  },
  (previous, next) =>
    previous.conversation.id === next.conversation.id &&
    previous.canAccessActions === next.canAccessActions &&
    previous.conversation.agentName === next.conversation.agentName &&
    previous.conversation.title === next.conversation.title &&
    previous.conversation.agentStatus === next.conversation.agentStatus &&
    previous.conversation.agentLastHeartbeatAt ===
      next.conversation.agentLastHeartbeatAt &&
    previous.conversation.avatar?.cut === next.conversation.avatar?.cut &&
    previous.conversation.avatar?.colour === next.conversation.avatar?.colour &&
    previous.conversation.avatar?.recoveryRequired ===
      next.conversation.avatar?.recoveryRequired &&
    previous.displayName === next.displayName &&
    previous.selected === next.selected &&
    previous.isMenuOpen === next.isMenuOpen &&
    previous.isCoarsePointer === next.isCoarsePointer &&
    previous.disabled === next.disabled &&
    previous.activityNow === next.activityNow &&
    previous.onSelect === next.onSelect &&
    previous.onOpenMenu === next.onOpenMenu,
);

AgentInventoryTile.displayName = "AgentInventoryTile";

/**
 * Extract the doc_id from an Azure Blob SAS URL.
 * Blob path: /container/ida-documents/{workspace_id}/{doc_uuid}
 * Doc ID in database: {workspace_id}/{doc_uuid}
 */
function extractDocIdFromSasUrl(url: string): string | null {
  try {
    const pathname = new URL(url).pathname;
    const parts = pathname.split("/");
    // Find ida-documents segment, then workspace_id and doc_uuid follow
    const idaIdx = parts.indexOf("ida-documents");
    if (idaIdx >= 0 && parts.length > idaIdx + 2) {
      const workspaceId = parts[idaIdx + 1];
      const docUuid = parts[idaIdx + 2];
      return `${workspaceId}/${docUuid}`;
    }
  } catch {
    // Not a valid URL
  }
  return null;
}

function triggerBrowserDownload(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Download callback type passed from React context into DOM event handlers. */
type DownloadFn = (imgSrc: string) => void;

// SVG paths for overlay buttons (Material Design icons)
const COPY_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>`;
const CHECK_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>`;
const DOWNLOAD_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>`;

/**
 * Style an <img> with overlay buttons and expired-image handling.
 * IMPORTANT: Never move the img element (no insertBefore/appendChild on it)
 * because React owns these DOM nodes. Only add siblings and modify styles.
 */
function decorateImage(img: HTMLImageElement, onDownload?: DownloadFn) {
  if (img.getAttribute("data-overlayed")) return;
  img.setAttribute("data-overlayed", "true");

  const parent = img.parentElement;
  if (!parent) return;

  // Style the parent as positioning context (don't move the img)
  parent.style.position = "relative";
  parent.style.display = "inline-block";
  parent.style.maxWidth = "min(100%, 360px)";
  img.style.maxWidth = "100%";
  img.style.borderRadius = "8px";
  img.style.display = "block";

  // Expired-image placeholder (inserted as sibling, hides img via CSS)
  const showExpiredPlaceholder = () => {
    img.style.display = "none";
    // Remove overlay if present
    const existingOverlay = parent.querySelector("[data-img-overlay]");
    if (existingOverlay) existingOverlay.remove();
    // Insert placeholder after img
    const placeholder = document.createElement("div");
    placeholder.style.cssText =
      "display:flex;flex-direction:column;align-items:center;justify-content:center;" +
      "gap:6px;padding:24px 16px;border-radius:8px;min-height:80px;" +
      "background:var(--joy-palette-neutral-100,#f0f0f0);" +
      "color:var(--joy-palette-neutral-500,#73738c);" +
      "font-size:13px;text-align:center;border:1px dashed " +
      "var(--joy-palette-neutral-300,#c4c4c4)";
    placeholder.innerHTML =
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" ` +
      `viewBox="0 0 24 24" fill="currentColor" style="opacity:0.5">` +
      `<path d="M21 5v6.59l-3-3.01-4 4.01-4-4-4 4-3-3.01V5c0-1.1.9-2 ` +
      `2-2h14c1.1 0 2 .9 2 2zm-3 6.42 3 3.01V19c0 1.1-.9 2-2 2H5c-1.1 ` +
      `0-2-.9-2-2v-6.58l3 2.99 4-4 4 4 4-3.99z"/></svg>` +
      `<span>Image expired</span>`;
    img.insertAdjacentElement("afterend", placeholder);
  };

  // If image already failed (cached error), show placeholder immediately
  if (img.complete && img.naturalWidth === 0 && img.src) {
    showExpiredPlaceholder();
    return;
  }
  img.addEventListener("error", showExpiredPlaceholder, { once: true });

  // Overlay (inserted as sibling after img, positioned absolutely over parent)
  const overlay = document.createElement("div");
  overlay.setAttribute("data-img-overlay", "true");
  overlay.style.cssText =
    "position:absolute;top:0;left:0;right:0;bottom:0;border-radius:8px;" +
    "background:linear-gradient(to bottom,rgba(0,0,0,0.5) 0%,transparent 40%);" +
    "opacity:0;transition:opacity 0.2s;display:flex;align-items:flex-start;" +
    "justify-content:flex-end;gap:4px;padding:6px;pointer-events:none";

  const btnStyle =
    "display:flex;align-items:center;justify-content:center;" +
    "width:28px;height:28px;border:none;border-radius:6px;cursor:pointer;" +
    "background:rgba(255,255,255,0.9);color:#555;pointer-events:auto;" +
    "transition:background 0.15s";

  // Copy button
  const copyBtn = document.createElement("button");
  copyBtn.style.cssText = btnStyle;
  copyBtn.title = "Copy image";
  copyBtn.innerHTML = COPY_ICON_SVG;
  copyBtn.onmouseenter = () => {
    copyBtn.style.background = "white";
  };
  copyBtn.onmouseleave = () => {
    copyBtn.style.background = "rgba(255,255,255,0.9)";
  };
  copyBtn.onclick = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(img.src);
      const blob = await res.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
      ]);
    } catch {
      navigator.clipboard.writeText(img.src);
    }
    copyBtn.innerHTML = CHECK_ICON_SVG;
    setTimeout(() => {
      copyBtn.innerHTML = COPY_ICON_SVG;
    }, 1500);
  };

  // Download button
  const downloadBtn = document.createElement("button");
  downloadBtn.style.cssText = btnStyle;
  downloadBtn.title = "Download image";
  downloadBtn.innerHTML = DOWNLOAD_ICON_SVG;
  downloadBtn.onmouseenter = () => {
    downloadBtn.style.background = "white";
  };
  downloadBtn.onmouseleave = () => {
    downloadBtn.style.background = "rgba(255,255,255,0.9)";
  };
  downloadBtn.onclick = (e) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload(img.src);
    }
  };

  overlay.appendChild(copyBtn);
  overlay.appendChild(downloadBtn);
  // Insert overlay as sibling after img (don't wrap img)
  img.insertAdjacentElement("afterend", overlay);

  // Show/hide overlay on parent hover
  parent.addEventListener("mouseenter", () => {
    overlay.style.opacity = "1";
    overlay.style.pointerEvents = "auto";
  });
  parent.addEventListener("mouseleave", () => {
    overlay.style.opacity = "0";
    overlay.style.pointerEvents = "none";
  });
}

/**
 * Wraps rendered message content and adds hover overlays (copy + download)
 * to any <img> elements found in the DOM.  Uses MutationObserver to catch
 * images added asynchronously by the markdown renderer.
 *
 * Downloads go through the backend proxy endpoint so they're same-origin
 * (correct filename via Content-Disposition, no CORS issues).
 */
export const ImageOverlayWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { workspace } = useWorkspaceContext();
  const downloadDocument = useDownloadDocument();
  const resolveDocumentDownloadLink = useResolveDocumentDownloadLink();

  // Build a stable download callback that uses the backend proxy
  const handleDownload = useCallback(
    async (imgSrc: string) => {
      const endpoint = toInternalDownloadLinkEndpoint(imgSrc);
      if (endpoint) {
        try {
          const link = await resolveDocumentDownloadLink(endpoint);
          triggerBrowserDownload(link.download_url, link.filename);
        } catch (error) {
          console.error(
            "Failed to resolve internal document download link",
            error,
          );
        }
        return;
      }

      if (!workspace?.id) return;
      const docId = extractDocIdFromSasUrl(imgSrc);
      if (docId) {
        // Use backend proxy -- same-origin, correct Content-Disposition
        downloadDocument(workspace.id, docId, docId);
      } else {
        // Fallback for non-SAS URLs: direct link
        triggerBrowserDownload(imgSrc, "image.png");
      }
    },
    [workspace?.id, downloadDocument, resolveDocumentDownloadLink],
  );

  const resolveInternalImageSrc = useCallback(
    async (img: HTMLImageElement) => {
      const originalSrc = img.getAttribute("src") || "";
      const endpoint = toInternalDownloadLinkEndpoint(originalSrc);
      if (!endpoint || img.getAttribute("data-internal-download-resolved")) {
        return;
      }

      img.setAttribute("data-internal-download-resolved", "pending");
      try {
        const link = await resolveDocumentDownloadLink(endpoint);
        img.src = link.download_url;
        if (!img.alt) img.alt = link.filename;
        img.setAttribute("data-internal-download-resolved", "true");
      } catch {
        img.setAttribute("data-internal-download-resolved", "error");
        img.dispatchEvent(new Event("error"));
      }
    },
    [resolveDocumentDownloadLink],
  );

  const handleContentClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest("a");
      const href = anchor?.getAttribute("href") || "";
      const endpoint = toInternalDownloadLinkEndpoint(href);
      if (!anchor || !endpoint) return;

      event.preventDefault();
      event.stopPropagation();
      void resolveDocumentDownloadLink(endpoint)
        .then((link) => {
          triggerBrowserDownload(link.download_url, link.filename);
        })
        .catch((error) => {
          console.error(
            "Failed to resolve internal document download link",
            error,
          );
        });
    },
    [resolveDocumentDownloadLink],
  );

  // Keep a ref to the latest callback so DOM event handlers always use it
  const downloadRef = useRef(handleDownload);
  downloadRef.current = handleDownload;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onDownload: DownloadFn = (src) => downloadRef.current(src);
    const processImage = (img: HTMLImageElement) => {
      if (img.getAttribute("data-overlayed")) return;
      if (toInternalDownloadLinkEndpoint(img.getAttribute("src") || "")) {
        void resolveInternalImageSrc(img).finally(() =>
          decorateImage(img, onDownload),
        );
        return;
      }
      decorateImage(img, onDownload);
    };

    // Decorate any images already in the DOM
    el.querySelectorAll<HTMLImageElement>("img:not([data-overlayed])").forEach(
      processImage,
    );

    // Watch for images added later (e.g. by the markdown renderer)
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLImageElement) {
            processImage(node);
          } else if (node instanceof HTMLElement) {
            node
              .querySelectorAll<HTMLImageElement>("img:not([data-overlayed])")
              .forEach(processImage);
          }
        }
      }
    });

    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [resolveInternalImageSrc]);

  return (
    <div ref={ref} onClickCapture={handleContentClick}>
      {children}
    </div>
  );
};

/** Expired image placeholder shown when an image URL is no longer valid. */
const ExpiredImagePlaceholder: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      py: 3,
      px: 2,
      borderRadius: "8px",
      minHeight: 80,
      bgcolor: "neutral.100",
      color: "neutral.500",
      fontSize: 13,
      textAlign: "center",
      border: "1px dashed",
      borderColor: "neutral.300",
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ opacity: 0.5 }}
    >
      <path d="M21 5v6.59l-3-3.01-4 4.01-4-4-4 4-3-3.01V5c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2zm-3 6.42 3 3.01V19c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2v-6.58l3 2.99 4-4 4 4 4-3.99z" />
    </svg>
    <span>Image expired</span>
  </Box>
);

/** Inline image for tool results (generate_image) with overlay and expiry handling. */
export const ToolResultImage: React.FC<{ src: string; alt: string }> = ({
  src,
  alt,
}) => {
  const [expired, setExpired] = React.useState(false);
  const [resolvedSrc, setResolvedSrc] = React.useState(() =>
    isConversationImageEndpoint(src) ? "" : src,
  );
  const objectUrlRef = useRef<string | null>(null);
  const { workspace } = useWorkspaceContext();
  const downloadDocument = useDownloadDocument();
  const resolveDocumentDownloadLink = useResolveDocumentDownloadLink();
  const authFetch = useAuthFetch("raw");

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;
    setExpired(false);

    const imageRoute = getConversationImageRoute(src);
    if (imageRoute) {
      setResolvedSrc("");
      if (!workspace?.id) {
        setExpired(true);
        return () => {
          cancelled = true;
        };
      }

      void authFetch(
        `/api/workspace/ida/chat/conversations/${imageRoute.conversationId}/images/${imageRoute.attachmentId}`,
        "load rendered artifact image",
        { headers: { "X-Workspace-Id": workspace.id } },
        30000,
        { suppressNetworkBanner: true },
      )
        .then((response: Response) => response.blob())
        .then((blob: Blob) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          objectUrlRef.current = objectUrl;
          setResolvedSrc(objectUrl);
        })
        .catch(() => {
          if (!cancelled) setExpired(true);
        });

      return () => {
        cancelled = true;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          if (objectUrlRef.current === objectUrl) objectUrlRef.current = null;
        }
      };
    }

    const endpoint = toInternalDownloadLinkEndpoint(src);
    if (!endpoint) {
      setResolvedSrc(src);
      return () => {
        cancelled = true;
      };
    }

    resolveDocumentDownloadLink(endpoint)
      .then((link) => {
        if (!cancelled) setResolvedSrc(link.download_url);
      })
      .catch(() => {
        if (!cancelled) setExpired(true);
      });

    return () => {
      cancelled = true;
    };
  }, [authFetch, src, workspace?.id, resolveDocumentDownloadLink]);

  const handleDownload = useCallback(() => {
    const endpoint = toInternalDownloadLinkEndpoint(src);
    if (endpoint) {
      void resolveDocumentDownloadLink(endpoint)
        .then((link) => {
          triggerBrowserDownload(link.download_url, link.filename);
        })
        .catch(() => setExpired(true));
      return;
    }

    if (isConversationImageEndpoint(src)) {
      if (resolvedSrc) {
        triggerBrowserDownload(resolvedSrc, `${alt || "rendered-image"}.jpg`);
      }
      return;
    }

    if (!workspace?.id) return;
    const docId = extractDocIdFromSasUrl(src);
    if (docId) {
      downloadDocument(workspace.id, docId, alt || docId);
    }
  }, [
    workspace?.id,
    downloadDocument,
    src,
    resolvedSrc,
    alt,
    resolveDocumentDownloadLink,
  ]);

  if (expired) {
    return (
      <Box sx={{ my: 1, maxWidth: "min(100%, 360px)" }}>
        <ExpiredImagePlaceholder />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        my: 1,
        position: "relative",
        display: "inline-block",
        maxWidth: "min(100%, 360px)",
        "&:hover .image-overlay": { opacity: 1 },
      }}
    >
      <img
        src={resolvedSrc || undefined}
        alt={alt}
        style={{ maxWidth: "100%", borderRadius: 8, display: "block" }}
        loading="lazy"
        onError={() => {
          setExpired(true);
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
          }
        }}
      />
      <Box
        className="image-overlay"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "8px",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 40%)",
          opacity: 0,
          transition: "opacity 0.2s",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end",
          gap: 0.5,
          p: 0.75,
        }}
      >
        <Tooltip title="Copy image">
          <IconButton
            size="sm"
            variant="solid"
            sx={{
              bgcolor: "rgba(255,255,255,0.9)",
              color: "neutral.700",
              "&:hover": { bgcolor: "white" },
              minWidth: 32,
              minHeight: 32,
            }}
            onClick={() => navigator.clipboard.writeText(resolvedSrc || src)}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download image">
          <IconButton
            size="sm"
            variant="solid"
            sx={{
              bgcolor: "rgba(255,255,255,0.9)",
              color: "neutral.700",
              "&:hover": { bgcolor: "white" },
              minWidth: 32,
              minHeight: 32,
            }}
            onClick={handleDownload}
          >
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

function getConversationImageRoute(
  src: string,
): { conversationId: string; attachmentId: string } | null {
  const match =
    /^\/api\/workspace\/ida\/chat\/conversations\/(\d+)\/images\/([^/?#]+)$/.exec(
      src,
    );
  return match ? { conversationId: match[1], attachmentId: match[2] } : null;
}

function isConversationImageEndpoint(src: string): boolean {
  return getConversationImageRoute(src) !== null;
}

export function buildToolResultImages(
  execution: ToolExecution,
  conversationId: number | null,
): React.ReactNode {
  const references = execution.result?.image_refs;
  if (!conversationId || !Array.isArray(references)) return null;

  const details = execution.result?.result as
    Record<string, unknown> | undefined;
  const artifact = details?.artifact as Record<string, unknown> | undefined;
  const filename =
    typeof artifact?.filename === "string" ? artifact.filename : execution.name;
  const imageOrderValue = details?.images_follow_in_this_order;
  const imageOrder = Array.isArray(imageOrderValue) ? imageOrderValue : [];
  const images = references.flatMap((reference, index) => {
    if (!reference || typeof reference !== "object") return [];
    const image = reference as Record<string, unknown>;
    if (
      typeof image.attachment_id !== "string" ||
      !image.attachment_id ||
      typeof image.media_type !== "string" ||
      !image.media_type.startsWith("image/")
    ) {
      return [];
    }

    const label =
      typeof imageOrder[index] === "string"
        ? imageOrder[index]
        : `image ${index + 1}`;
    const endpoint = `/api/workspace/ida/chat/conversations/${conversationId}/images/${encodeURIComponent(image.attachment_id)}`;
    return [
      <ToolResultImage
        key={endpoint}
        src={endpoint}
        alt={`${filename} ${label}`}
      />,
    ];
  });

  return images.length ? <Stack spacing={1}>{images}</Stack> : null;
}

/** Tool results belong to the conversation that owns the visible messages.
 * Selection changes before history loading completes, so prefer that owner. */
export function getToolResultConversationId(
  selectedConversationId: number | null,
  visibleMessagesConversationId: number | null,
): number | null {
  return visibleMessagesConversationId ?? selectedConversationId;
}

/** Simple Material Icons font wrapper that works with Joy UI */
const MaterialIcon: React.FC<{ name: string; sx?: object }> = ({
  name,
  sx,
}) => (
  <span className="material-icons" style={sx as React.CSSProperties}>
    {name}
  </span>
);

/** Props for the memoized message list component */
interface MessageListProps {
  messages: IDAChatMessage[];
  toolComposingLabel?: string | null;
  hasOlderMessages?: boolean;
  onLoadOlderMessages?: () => void;
  olderMessagesLoading?: boolean;
  /** True when the most recent "Load earlier messages" attempt failed. The button stays available to retry and the currently visible window is left untouched. */
  olderMessagesLoadFailed?: boolean;
  currentUserId?: string | null;
  contentRenderers?: Record<string, ContentRenderer>;
  onCitationClick?: (citation: Citation) => void;
  messageExtensions?: ChatMessageExtensions;
  messageIconFor?: (message: IDAChatMessage) => ChatIcons;
  toolExtensions?: ChatToolExecutionExtensions;
  chatEndRef: React.RefObject<HTMLDivElement>;
  /** Signature that changes when tool executions change, forcing re-renders */
  toolExecutionSignature?: string;
  /** ID of message currently being edited */
  editingMessageId?: string;
  skillAttributionEnabled?: boolean;
  skillAttributionsByTurnId?: Record<number, SkillInvocationAttribution[]>;
}

type PresentationMessage = IDAChatMessage & {
  sourceRole?: IDAChatMessage["role"];
};

// Message alignment still uses the package roles. IDA supplies a resolved
// human or agent icon per message; unknown identities intentionally remain
// iconless rather than receiving a misleading generic package marker.
const IDA_MESSAGE_ICONS: ChatIcons = {
  user: null,
  assistant: null,
};

const getTrustedSenderUserId = (message: IDAChatMessage): string | null => {
  const senderUserId = message.metadata?.sender_user_id;
  return typeof senderUserId === "string" ? senderUserId : null;
};

/** The chat package uses user role for right alignment; Hub delegation does not imply ownership. */
export const getMessagePresentationRole = (
  message: IDAChatMessage,
  currentUserId?: string | null,
): IDAChatMessage["role"] => {
  if (message.role !== "user") return message.role;
  const senderUserId = getTrustedSenderUserId(message);
  const isCurrentUser =
    Boolean(currentUserId) && senderUserId === currentUserId;
  return isCurrentUser ? "user" : "assistant";
};

function normalizeVoiceModelLabel(model: string): string {
  return /^gpt-live(?:-1)?$/i.test(model.trim()) ? "GPT-Live" : model;
}

const toPresentationMessage = (
  message: IDAChatMessage,
  currentUserId?: string | null,
): PresentationMessage => {
  const role = getMessagePresentationRole(message, currentUserId);
  return role === message.role
    ? message
    : { ...message, role, sourceRole: message.role };
};

/**
 * Memoized wrapper for individual chat messages.
 * Uses custom comparison to only re-render when message content actually changes.
 */
interface MemoizedChatMessageProps {
  message: IDAChatMessage;
  workingLabel?: string | null;
  contentRenderers?: Record<string, ContentRenderer>;
  onCitationClick?: (citation: Citation) => void;
  extensions?: ChatMessageExtensions;
  messageIconFor?: (message: IDAChatMessage) => ChatIcons;
  toolExtensions?: ChatToolExecutionExtensions;
}

/**
 * React.memo comparator for MemoizedChatMessage. Returns true when the props
 * are equal and the re-render can be SKIPPED. Exported for unit testing.
 */
export function areChatMessagePropsEqual(
  prevProps: MemoizedChatMessageProps,
  nextProps: MemoizedChatMessageProps,
): boolean {
  const prevMsg = prevProps.message;
  const nextMsg = nextProps.message;

  if (prevProps.workingLabel !== nextProps.workingLabel) return false;

  // Always re-render streaming messages (content is changing)
  if (nextMsg.isStreaming) return false;

  // The header is derived from the current agent inventory. On a persisted
  // reload that inventory can arrive after messages, so an unchanged message
  // must re-render when its extension closure gains the matching agent.
  if (prevProps.extensions !== nextProps.extensions) return false;
  if (prevProps.messageIconFor !== nextProps.messageIconFor) return false;

  // For non-streaming messages, compare by reference first (fast path)
  if (prevMsg === nextMsg) return true;

  // Then compare key fields that would require re-render
  return (
    prevMsg.id === nextMsg.id &&
    prevMsg.isStreaming === nextMsg.isStreaming &&
    prevMsg.content === nextMsg.content &&
    prevMsg.images === nextMsg.images &&
    prevMsg.overCap?.sizeClass === nextMsg.overCap?.sizeClass &&
    prevMsg.role === nextMsg.role &&
    prevMsg.interrupted === nextMsg.interrupted &&
    prevMsg.stoppedByUser === nextMsg.stoppedByUser &&
    prevMsg.toolExecutions === nextMsg.toolExecutions &&
    prevMsg.citations === nextMsg.citations &&
    // job_completion cards live-update via a replaced jobCompletion object
    // (e.g. aiResponsePending true->false when the auto-continue stream
    // settles). Without this check the memo treats the flag flip as "equal"
    // -- since id/content/role/toolExecutions/citations are unchanged -- and
    // React skips the re-render, leaving the "AI is processing result..."
    // spinner stuck even though the store flag is already cleared. The setter
    // replaces the jobCompletion object, so reference equality detects it.
    prevMsg.jobCompletion === nextMsg.jobCompletion
  );
}

/**
 * #5327: between submit and the first content frame the streaming assistant
 * bubble is empty and shows only a blinking cursor, which looks the same as a
 * broken app when the model is slow or failing. Show an explicit working state
 * until content, a tool, or a terminal frame replaces it.
 */
export function isAwaitingFirstContent(message: IDAChatMessage): boolean {
  return (
    message.role === "assistant" &&
    message.isStreaming === true &&
    !message.content &&
    !(message.toolExecutions && message.toolExecutions.length > 0)
  );
}

const MemoizedChatMessage = React.memo(function MemoizedChatMessage({
  message,
  workingLabel,
  contentRenderers,
  onCitationClick,
  extensions,
  messageIconFor,
  toolExtensions,
}: MemoizedChatMessageProps) {
  return (
    <>
      <ChatMessage
        showTimestamps
        message={message}
        icons={messageIconFor?.(message) ?? IDA_MESSAGE_ICONS}
        contentRenderers={contentRenderers}
        onCitationClick={onCitationClick}
        extensions={extensions}
        toolExtensions={toolExtensions}
      />
      {isAwaitingFirstContent(message) && (
        <ToolComposingIndicator label={workingLabel} />
      )}
      {/* #4981: a persisted interrupted answer must not read as complete
          after a reload. The mapper carries metadata.interrupted through;
          render the same notice the live stream appends. */}
      {message.role === "assistant" && message.interrupted === true && (
        <Typography
          level="body-sm"
          data-testid="interrupted-notice"
          role="note"
          sx={{ color: "warning.600", mt: 0.5, mx: 4 }}
        >
          {message.stoppedByUser
            ? STOPPED_BY_USER_NOTICE_TEXT
            : INTERRUPTED_NOTICE_TEXT}
        </Typography>
      )}
    </>
  );
}, areChatMessagePropsEqual);

/**
 * Memoized message list component to prevent re-renders during typing.
 * Only re-renders when messages or handlers change, not on input keystroke.
 * The toolExecutionSignature prop forces re-renders when tool executions change,
 * ensuring badges appear immediately when tools start running.
 */
const MessageList = React.memo<MessageListProps>(function MessageList({
  messages,
  toolComposingLabel = null,
  hasOlderMessages = false,
  onLoadOlderMessages,
  olderMessagesLoading = false,
  olderMessagesLoadFailed = false,
  currentUserId,
  contentRenderers,
  onCitationClick,
  messageExtensions,
  messageIconFor,
  toolExtensions,
  chatEndRef,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  toolExecutionSignature, // Used by React.memo to detect tool execution changes
  editingMessageId,
  skillAttributionEnabled,
  skillAttributionsByTurnId,
}) {
  void skillAttributionEnabled;
  void skillAttributionsByTurnId;
  const awaitingFirstContentMessageId = [...messages]
    .reverse()
    .find(isAwaitingFirstContent)?.id;
  const loggedDuplicateKeysRef = useRef(new Set<string>());
  if (process.env.NODE_ENV === "development") {
    findDuplicateMessageKeyDiagnostics(messages).forEach((diagnostic) => {
      const signature = `${diagnostic.id}:${diagnostic.sources.join(",")}`;
      if (loggedDuplicateKeysRef.current.has(signature)) return;
      loggedDuplicateKeysRef.current.add(signature);
      console.debug(
        "[IDA Chat] duplicate message list key",
        serializeDuplicateMessageKeyDiagnostic(diagnostic),
      );
    });
  }
  return (
    <Box sx={{ p: 2, pb: 2 }}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Stack spacing={2}>
          {hasOlderMessages && onLoadOlderMessages && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pb: 1,
                gap: 0.5,
              }}
            >
              <Button
                size="sm"
                variant="plain"
                onClick={onLoadOlderMessages}
                loading={olderMessagesLoading}
                disabled={olderMessagesLoadFailed}
                aria-label="Load earlier messages"
              >
                Load earlier messages
              </Button>
              {olderMessagesLoadFailed && (
                <Typography
                  data-testid="older-messages-load-failed"
                  level="body-xs"
                  sx={{ color: "danger.500" }}
                >
                  Earlier messages could not be loaded.
                </Typography>
              )}
            </Box>
          )}
          {messages.map((message) => {
            const presentationMessage = toPresentationMessage(
              message,
              currentUserId,
            );
            const isEditing = editingMessageId === message.id;
            const heartbeatMetadata = getHeartbeatMetadata(message);
            return (
              <Box
                key={message.id}
                sx={
                  isEditing
                    ? {
                        bgcolor: "primary.softBg",
                        borderRadius: 1,
                        mx: -1,
                        px: 1,
                        py: 0.5,
                        border: "1px solid",
                        borderColor: "primary.outlinedBorder",
                      }
                    : undefined
                }
              >
                {message.overCap ? (
                  <Box
                    data-testid={`over-cap-message-${message.id}`}
                    role="note"
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "neutral.outlinedBorder",
                      borderRadius: 1,
                      userSelect: "none",
                    }}
                  >
                    <Typography level="body-md">
                      This message is too large to display
                    </Typography>
                    <Typography level="body-xs">
                      ID: {message.id} · {message.overCap.sizeClass}
                    </Typography>
                  </Box>
                ) : heartbeatMetadata ? (
                  <HeartbeatTriggerCard
                    createdAt={message.createdAt}
                    taskId={heartbeatMetadata.task_id}
                  />
                ) : (
                  <MemoizedChatMessage
                    message={presentationMessage}
                    workingLabel={
                      message.id === awaitingFirstContentMessageId
                        ? toolComposingLabel
                        : null
                    }
                    contentRenderers={contentRenderers}
                    onCitationClick={onCitationClick}
                    extensions={messageExtensions}
                    messageIconFor={messageIconFor}
                    toolExtensions={toolExtensions}
                  />
                )}
              </Box>
            );
          })}
          {toolComposingLabel && !awaitingFirstContentMessageId && (
            <ToolComposingIndicator label={toolComposingLabel} />
          )}
          <div ref={chatEndRef} />
        </Stack>
      </Box>
    </Box>
  );
});

// Stable default so the prop does not change identity on every render.
const EMPTY_FOLLOW_UP_SUGGESTIONS: string[] = [];

interface FullPageChatLayoutProps {
  messages: IDAChatMessage[];
  toolComposingLabel?: string | null;
  visibleMessagesConversationId?: number | null;
  hasOlderMessages?: boolean;
  onLoadOlderMessages?: () => void;
  olderMessagesLoading?: boolean;
  /** True when the most recent "Load earlier messages" attempt failed. The button stays available to retry and the currently visible window is left untouched. */
  olderMessagesLoadFailed?: boolean;
  onSendMessage: (
    message: string,
    images?: ImageReference[],
    onAccepted?: () => void,
  ) => void | Promise<void>;
  /** Capture a plain-text draft so the host can restore it if the POST fails before streaming starts. */
  onTextSendStarted?: (draft: string, restoreDraft: () => void) => void;
  /** Submit a message to the server queue while another turn is active. */
  onQueueMessage?: (
    message: string,
    images?: ImageReference[],
  ) => void | Promise<void>;
  queuedMessages?: ConversationQueueItem[];
  queueError?: string | null;
  onCancelQueuedMessage?: (item: ConversationQueueItem) => void | Promise<void>;
  onCancelAllQueuedMessages?: () => void | Promise<void>;
  imageAttachmentsEnabled?: boolean;
  uploadImage?: (file: File) => Promise<ImageReference>;
  isLoading: boolean;
  isStopping?: boolean;
  conversations: CollaborationConversationSummary[];
  onLoadConversation: (id: number) => void;
  onNewConversation: () => void;
  onSearchConversations: (query: string) => void;
  onLoadMoreConversations: () => void | boolean | Promise<void | boolean>;
  hasMoreConversations?: boolean;
  onDeleteConversation?: (id: number) => Promise<void>;
  /**
   * Rename a conversation. Called from the inline-rename UI in the history
   * sidebar and the active chat header. The implementation is expected to
   * trim, validate non-empty, do an optimistic local update, and roll back
   * on error.
   */
  onRenameConversation?: (id: number, nextTitle: string) => Promise<void>;
  onUpdateAgentConversation?: (
    id: number,
    patch: {
      agentName?: string;
      systemPromptOverride?: string | null;
      personaSourceId?: string;
    },
  ) => Promise<void>;
  onAssignAgentAvatar?: (
    id: number,
    selection: {
      selected_cut: AgentAvatarCut;
      selected_colour: AgentAvatarColour;
    },
  ) => Promise<void>;
  onGetAgentAvatarReservations?: (
    id: number,
  ) => Promise<AgentAvatarReservations>;
  onReleaseAgentAvatar?: (id: number) => Promise<void>;
  conversationsLoading: boolean;
  currentConversationId: string | number | null;
  contentRenderers?: Record<string, ContentRenderer>;
  onCitationClick?: (citation: Citation) => void;
  /** Fetch usage data for a message (lazy loading) */
  fetchMessageUsage?: (
    conversationId: number,
    messageId: number,
  ) => Promise<MessageUsage | null>;
  // Workspace personalization props
  title?: string;
  welcomeTitle?: string;
  welcomeDescription?: string;
  /** True when the active conversation's history failed to load (e.g. a gateway timeout). */
  historyLoadFailed?: boolean;
  /** True while the active conversation's history is being fetched. Takes precedence over historyLoadFailed and the welcome content. */
  historyLoading?: boolean;
  /** Re-attempt loading the active conversation's history after a failure. Rendered as a retry affordance beside the load-failed message when provided. */
  onRetryHistoryLoad?: () => void;
  /** Set when the background message-sync poll halted on a non-retryable error (413). Rendered as a dismissable banner; unlike historyLoadFailed, the already-loaded message list stays visible. */
  messagesSyncError?: string | null;
  /** Dismiss the messagesSyncError banner. */
  onDismissMessagesSyncError?: () => void;
  inputPlaceholder?: string;
  iconSuggestions?: string[];
  onHelpClick?: () => void;
  // File upload props
  onSendMessageWithFiles?: (
    message: string,
    files: File[],
    images?: ImageReference[],
  ) => Promise<void>;
  isUploading?: boolean;
  // Message action props - delete from a message (inclusive) for edit/delete functionality
  onDeleteMessagesFrom?: (message: ChatMessageType) => Promise<void>;
  // Stream control
  onCancelStream?: () => void;
  /** Disable all chat input (e.g. when quota is exceeded) */
  chatDisabled?: boolean;
  /** A selected conversation is loading; input and agent actions must wait. */
  conversationSwitching?: boolean;
  /** A New Agent click has cleared the old conversation but has not started onboarding yet. */
  agentCreationPending?: boolean;
  /** Placeholder shown when chatDisabled is true */
  chatDisabledMessage?: string;
  /** Sticky content rendered at the top of the scrollable chat area */
  stickyTopContent?: React.ReactNode;
  skillAttributionEnabled?: boolean;
  skillAttributionsByTurnId?: Record<number, SkillInvocationAttribution[]>;
  /** Follow-up questions for the newest answer; clicking one sends it. */
  followUpSuggestions?: string[];
  collaborationEnabled?: boolean;
  currentUser?: User | null;
  workspaceUsers?: WorkspaceUser[];
  currentConversationCollaborators?: ConversationCollaborator[];
  conversationCollaboratorsById?: Record<number, ConversationCollaborator[]>;
  conversationCollaboratorsLoading?: boolean;
  conversationCollaboratorsError?: string | null;
  canManageCollaborators?: boolean;
  onAddCollaborators?: (userIds: string[]) => Promise<void>;
  onRemoveCollaborator?: (userId: string) => Promise<void>;
  onRestoreMessageFiles?: (message: ChatMessageWithFiles) => Promise<File[]>;
  /** Render the voice input mic button (feature-flagged at the Chat container). */
  voiceInputEnabled?: boolean;
  /** Render the separate GPT-Live wave button in the composer. */
  gptLiveVoiceControl?: React.ReactNode;
  /** Voice feedback rendered above the composer row at full composer width. */
  gptLiveVoiceFeedback?: {
    message: string;
    tone: "danger" | "neutral";
  } | null;
  /** Workspace ID required by the voice input token endpoint. */
  workspaceId?: string;
  /** Workspace mode controls chat-vs-agent labeling for this scaffold. */
  workspaceMode?: "chat" | "agent";
  /** Keep the workspace setup conversation easy to resume while onboarding. */
  needsOnboarding?: boolean;
  onboardingAgentId?: number | null;
  workspaceRole?: string | null;
  /** Actual backend listener readiness reported by the status-stream snapshot. */
  agentStatusPushActive?: boolean | null;
}

export const FullPageChatLayout: React.FC<FullPageChatLayoutProps> = ({
  messages,
  toolComposingLabel = null,
  visibleMessagesConversationId,
  hasOlderMessages = false,
  onLoadOlderMessages,
  olderMessagesLoading = false,
  olderMessagesLoadFailed = false,
  onSendMessage,
  onTextSendStarted,
  onQueueMessage,
  queuedMessages = [],
  queueError = null,
  onCancelQueuedMessage,
  onCancelAllQueuedMessages,
  imageAttachmentsEnabled = false,
  uploadImage,
  isLoading,
  isStopping = false,
  conversations,
  onLoadConversation,
  onNewConversation,
  onSearchConversations,
  onLoadMoreConversations,
  hasMoreConversations = false,
  onDeleteConversation,
  onRenameConversation,
  onUpdateAgentConversation,
  onAssignAgentAvatar,
  onGetAgentAvatarReservations,
  onReleaseAgentAvatar,
  conversationsLoading,
  currentConversationId,
  contentRenderers,
  onCitationClick,
  fetchMessageUsage,
  title,
  welcomeTitle,
  welcomeDescription,
  historyLoadFailed = false,
  historyLoading = false,
  onRetryHistoryLoad,
  messagesSyncError = null,
  onDismissMessagesSyncError,
  inputPlaceholder,
  iconSuggestions,
  onHelpClick,
  onSendMessageWithFiles,
  isUploading = false,
  onDeleteMessagesFrom,
  onCancelStream,
  chatDisabled = false,
  conversationSwitching = false,
  agentCreationPending = false,
  chatDisabledMessage = "Monthly request limit reached. Upgrade your plan to continue.",
  stickyTopContent,
  skillAttributionEnabled = true,
  skillAttributionsByTurnId = {},
  followUpSuggestions = EMPTY_FOLLOW_UP_SUGGESTIONS,
  collaborationEnabled = false,
  currentUser = null,
  workspaceUsers = [],
  currentConversationCollaborators = [],
  conversationCollaboratorsById = {},
  conversationCollaboratorsLoading = false,
  conversationCollaboratorsError = null,
  canManageCollaborators = false,
  onAddCollaborators,
  onRemoveCollaborator,
  onRestoreMessageFiles,
  voiceInputEnabled = false,
  gptLiveVoiceControl,
  gptLiveVoiceFeedback = null,
  workspaceId,
  workspaceMode = "chat",
  needsOnboarding = false,
  onboardingAgentId = null,
  workspaceRole = null,
  agentStatusPushActive = null,
}) => {
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyTooltipOpen, setHistoryTooltipOpen] = React.useState(false);

  useEffect(() => {
    if (!shouldShowHistoryPill) return;
    setHistoryTooltipOpen(true);
    const timer = setTimeout(() => setHistoryTooltipOpen(false), 2000);
    return () => {
      clearTimeout(timer);
      setHistoryTooltipOpen(false);
    };
  }, []);

  const [collaboratorModalOpen, setCollaboratorModalOpen] =
    React.useState(false);
  const [collaborationSaving, setCollaborationSaving] = React.useState(false);
  const [collaborationNotice, setCollaborationNotice] = React.useState<
    string | null
  >(null);
  const [deleteMessageConfirm, setDeleteMessageConfirm] =
    React.useState<IDAChatMessage | null>(null);
  const [editingMessage, setEditingMessage] =
    React.useState<IDAChatMessage | null>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [editAttachmentRestoreState, setEditAttachmentRestoreState] =
    React.useState<"idle" | "restoring" | "failed" | "done">("idle");
  const [editingOriginalAttachmentCount, setEditingOriginalAttachmentCount] =
    React.useState(0);
  // Get first icon suggestion or default to chat_bubble_outline
  const welcomeIconName = iconSuggestions?.[0] || "chat_bubble_outline";
  const [searchQuery, setSearchQuery] = React.useState("");
  const [hoveredConvoId, setHoveredConvoId] = React.useState<number | null>(
    null,
  );
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<number | null>(
    null,
  );
  const [deleteConversationError, setDeleteConversationError] = React.useState<
    string | null
  >(null);
  const [avatarPickerConversation, setAvatarPickerConversation] =
    React.useState<CollaborationConversationSummary | null>(null);
  const [avatarPickerCut, setAvatarPickerCut] =
    React.useState<AgentAvatarCut | null>(null);
  const [avatarPickerColour, setAvatarPickerColour] =
    React.useState<AgentAvatarColour | null>(null);
  const [avatarReservations, setAvatarReservations] =
    React.useState<AgentAvatarReservations | null>(null);
  const [avatarAvailabilityLoading, setAvatarAvailabilityLoading] =
    React.useState(false);
  const [avatarAvailabilityError, setAvatarAvailabilityError] = React.useState<
    string | null
  >(null);
  const [avatarActionError, setAvatarActionError] = React.useState<
    string | null
  >(null);
  const availableAvatarCuts = React.useMemo(
    () =>
      avatarReservations
        ? AGENT_AVATAR_CUTS.filter(
            (cut) => !avatarReservations.reservedCuts.includes(cut),
          )
        : [],
    [avatarReservations],
  );
  const availableAvatarColours = React.useMemo(
    () =>
      avatarReservations
        ? AGENT_AVATAR_COLOURS.filter(
            (colour) => !avatarReservations.reservedColours.includes(colour),
          )
        : [],
    [avatarReservations],
  );
  const [avatarActionSaving, setAvatarActionSaving] = React.useState(false);
  // Inline-rename state. `renamingConvoId === null` for list items means the
  // sidebar list is in display mode for every row; a number means that row's
  // title is being edited. `headerRenaming` separately tracks rename in the
  // active chat header (independent of the sidebar so users can edit either
  // surface).
  const [renamingConvoId, setRenamingConvoId] = React.useState<number | null>(
    null,
  );
  const [renameDraft, setRenameDraft] = React.useState<string>("");
  const [renameSubmitting, setRenameSubmitting] = React.useState(false);
  const [headerRenaming, setHeaderRenaming] = React.useState(false);
  const [headerRenameDraft, setHeaderRenameDraft] = React.useState<string>("");
  const [agentMenuAnchor, setAgentMenuAnchor] =
    React.useState<HTMLElement | null>(null);
  const [agentMenuConversation, setAgentMenuConversation] =
    React.useState<CollaborationConversationSummary | null>(null);
  const agentMenuAnchorRef = React.useRef<HTMLElement | null>(null);
  const agentMenuConversationIdRef = React.useRef<number | null>(null);
  const [personaConversation, setPersonaConversation] =
    React.useState<CollaborationConversationSummary | null>(null);
  const [personaNameDraft, setPersonaNameDraft] = React.useState("");
  const [personaPromptDraft, setPersonaPromptDraft] = React.useState("");
  const [personaSubmitting, setPersonaSubmitting] = React.useState(false);
  const [personaError, setPersonaError] = React.useState<string | null>(null);
  const [personaLibrary, setPersonaLibrary] =
    React.useState<ReusablePersonaGroups>({ own: [], shared: [] });
  const [personaLibraryLoading, setPersonaLibraryLoading] =
    React.useState(false);
  const [personaLibraryError, setPersonaLibraryError] = React.useState<
    string | null
  >(null);
  const [personaLibraryName, setPersonaLibraryName] = React.useState("");
  const [personaLibraryShared, setPersonaLibraryShared] = React.useState(false);
  const [personaEditing, setPersonaEditing] =
    React.useState<ReusablePersona | null>(null);
  const [personaEditName, setPersonaEditName] = React.useState("");
  const [personaEditBody, setPersonaEditBody] = React.useState("");
  const [personaEditShared, setPersonaEditShared] = React.useState(false);
  const personas = usePersonaLibrary();
  const [copiedMessageId, setCopiedMessageId] = React.useState<string | null>(
    null,
  );
  const chatEndRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const stickyTopContentRef = useRef<HTMLDivElement>(null);
  const agentInventoryRef = useRef<HTMLDivElement>(null);
  const agentInventoryScrollRef = useRef<HTMLDivElement>(null);
  const [agentInventoryLoadFailed, setAgentInventoryLoadFailed] =
    React.useState(false);
  const [agentInventoryHeight, setAgentInventoryHeight] = React.useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editRestoreRequestIdRef = useRef(0);
  const [headerHeight, setHeaderHeight] = React.useState(0);
  const [bannerHeight, setBannerHeight] = React.useState(0);
  const [composerHeight, setComposerHeight] = React.useState(0);
  const [stickyTopContentHeight, setStickyTopContentHeight] = React.useState(0);
  const [upperbarHeight, setUpperbarHeight] = React.useState(56);
  const modeLabels = useMemo(() => {
    if (workspaceMode === "agent") {
      return {
        historyTooltip: "Agent inventory",
        headerFallbackTitle: "IDA Agent",
        renameTooltip: "Rename agent",
        renameAriaLabel: "Rename agent",
        newTooltip: "New agent",
        newButton: "New Agent",
        drawerTitle: "Agents",
        searchPlaceholder: "Search agents...",
        drawerNewItem: "New Agent",
        emptyList: "No agents yet",
        rowFallback: "Agent",
        deleteTitle: "Delete agent?",
        deleteBody:
          "This will permanently delete this agent and all its messages.",
      };
    }
    return {
      historyTooltip: "Conversation history",
      headerFallbackTitle: "IDA Chat",
      renameTooltip: "Rename chat",
      renameAriaLabel: "Rename chat",
      newTooltip: "New conversation",
      newButton: "New Chat",
      drawerTitle: "Conversations",
      searchPlaceholder: "Search conversations...",
      drawerNewItem: "New conversation",
      emptyList: "No conversations yet",
      rowFallback: "Conversation",
      deleteTitle: "Delete conversation?",
      deleteBody:
        "This will permanently delete this conversation and all its messages.",
    };
  }, [workspaceMode]);

  // File attachment state
  const [attachedFiles, setAttachedFiles] = React.useState<File[]>([]);
  const [images, setImages] = React.useState<SelectedImage[]>([]);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [imageAttemptCount, setImageAttemptCount] = React.useState<
    number | null
  >(null);
  const latestMessage = messages[messages.length - 1] as
    (IDAChatMessage & { errorCode?: string }) | undefined;
  const serverImageRefusal =
    imageAttemptCount !== null &&
    messages.length > imageAttemptCount &&
    latestMessage?.role === "assistant" &&
    (latestMessage.errorCode === "IMAGE_INPUT_UNSUPPORTED" ||
      latestMessage.errorCode === "IMAGE_INPUT_DISABLED")
      ? latestMessage.content
      : null;
  const attachments = useMemo<SelectedAttachment[]>(
    () => [
      ...images.map((image) => ({ ...image, kind: "image" as const })),
      ...attachedFiles.map((file) => ({
        kind: "document" as const,
        reference: file,
        fileName: file.name,
        size: file.size,
        mediaType: file.type,
      })),
    ],
    [images, attachedFiles],
  );
  const handleAttachmentsChange = useCallback((next: SelectedAttachment[]) => {
    setImages(
      next.flatMap((item) =>
        item.kind === "image"
          ? [
              {
                reference: item.reference,
                fileName: item.fileName,
                previewUrl: item.previewUrl,
              },
            ]
          : [],
      ),
    );
    setAttachedFiles(
      next
        .filter((item) => item.kind === "document")
        .map((item) => item.reference as File),
    );
    setFileError(null);
    setImageAttemptCount(null);
  }, []);
  // An unsent selection belongs to the conversation it was made in. The
  // picker releases the preview URLs when the controlled selection changes,
  // so the host only has to empty it.
  const attachmentConversationIdRef = useRef(currentConversationId);
  useEffect(() => {
    if (attachmentConversationIdRef.current === currentConversationId) return;
    attachmentConversationIdRef.current = currentConversationId;
    setImages([]);
    setAttachedFiles([]);
    setFileError(null);
    setImageAttemptCount(null);
  }, [currentConversationId]);
  const currentConversation = useMemo(
    () => getConversationById(conversations, currentConversationId),
    [conversations, currentConversationId],
  );
  const activeConversationIdNumber = useMemo(() => {
    if (typeof currentConversationId === "number") return currentConversationId;
    if (typeof currentConversationId === "string") {
      const parsed = Number.parseInt(currentConversationId, 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }, [currentConversationId]);
  const agentMessageConversations = useMemo(
    () =>
      conversations
        .filter((conversation) => conversation.kind === "agent")
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id),
    [conversations],
  );
  const agentConversations = useMemo(
    () => (workspaceMode === "agent" ? agentMessageConversations : []),
    [agentMessageConversations, workspaceMode],
  );
  const agentActivityNow = useAgentActivityClock(agentMessageConversations);
  const visibleAgentConversationsById = useMemo(
    () =>
      new Map(
        agentMessageConversations.map((conversation) => [
          conversation.id,
          conversation,
        ]),
      ),
    [agentMessageConversations],
  );
  const currentChatAgent = useMemo(
    () =>
      workspaceMode === "agent" && activeConversationIdNumber !== null
        ? visibleAgentConversationsById.get(activeConversationIdNumber)
        : undefined,
    [activeConversationIdNumber, visibleAgentConversationsById, workspaceMode],
  );
  const shouldShowComposer =
    workspaceMode !== "agent" ||
    !!currentChatAgent ||
    isLoading ||
    agentCreationPending;
  const resolveAgentMessagePresentation = useCallback(
    (message: IDAChatMessage) => {
      const senderAgentId = /^agent:(\d+)$/.exec(
        getTrustedSenderUserId(message) || "",
      );
      let agent: CollaborationConversationSummary | undefined;
      if (senderAgentId) {
        // Delegated provenance wins over the currently selected conversation.
        agent = visibleAgentConversationsById.get(Number(senderAgentId[1]));
      } else {
        const sourceRole =
          (message as PresentationMessage).sourceRole ?? message.role;
        agent =
          workspaceMode === "agent" &&
          (sourceRole === "assistant" || sourceRole === "tool")
            ? currentChatAgent
            : undefined;
      }
      return agent
        ? {
            agent,
            bubbleTone: getAgentGemstoneBubbleTone(agent.id, {
              cut: agent.avatar?.cut,
              colour: agent.avatar?.colour,
            }),
          }
        : undefined;
    },
    [currentChatAgent, visibleAgentConversationsById, workspaceMode],
  );
  const messageIconFor = useCallback(
    (message: IDAChatMessage): ChatIcons => {
      const senderUserId = getTrustedSenderUserId(message);
      const isCurrentUserMessage =
        Boolean(currentUser?.id) && senderUserId === currentUser?.id;
      if (isCurrentUserMessage) {
        const displayName =
          currentUser?.name?.trim() || currentUser?.email?.trim() || "User";
        return {
          user: (
            <Avatar
              size="sm"
              role="img"
              aria-label={`${displayName} avatar`}
              title={`${displayName} avatar`}
              data-testid={`current-user-message-avatar-${message.id}`}
              sx={{ width: 32, height: 32, fontSize: "0.75rem" }}
            >
              {getUserInitials(displayName)}
            </Avatar>
          ),
          assistant: null,
          tool: null,
        };
      }
      const presentation = resolveAgentMessagePresentation(message);
      const agent = presentation?.agent;
      if (!agent) return IDA_MESSAGE_ICONS;

      const appearance = (
        <AgentGemstone
          id={agent.id}
          cut={agent.avatar?.cut}
          colour={agent.avatar?.colour}
          agentStatus={agent.agentStatus}
          agentLastHeartbeatAt={agent.agentLastHeartbeatAt}
          activity={resolveAgentActivityDisplay(
            agent.agentStatus,
            agent.agentLastHeartbeatAt,
            agentActivityNow,
          )}
          size={32}
          title={`${agent.agentName || agent.title || "Agent"} appearance`}
          testId={`agent-message-avatar-${message.id}`}
        />
      );
      return {
        user: null,
        assistant: appearance,
        tool: appearance,
      };
    },
    [
      currentUser?.email,
      currentUser?.id,
      agentActivityNow,
      currentUser?.name,
      resolveAgentMessagePresentation,
    ],
  );
  const isCoarsePointer = usePointerCoarse();
  const acceptedWorkspaceMemberCount = useMemo(
    () => workspaceUsers.filter((user) => user.status === "accepted").length,
    [workspaceUsers],
  );
  const hasOtherAcceptedWorkspaceMembers = acceptedWorkspaceMemberCount > 1;

  useEffect(() => {
    agentMenuAnchorRef.current = agentMenuAnchor;
    agentMenuConversationIdRef.current = agentMenuConversation?.id ?? null;
  }, [agentMenuAnchor, agentMenuConversation]);

  const requestMoreConversations = useCallback(() => {
    setAgentInventoryLoadFailed(false);
    try {
      const result = onLoadMoreConversations();
      if (typeof result === "boolean") {
        setAgentInventoryLoadFailed(!result);
      } else if (result) {
        void result
          .then((loaded) => {
            if (loaded === false) setAgentInventoryLoadFailed(true);
          })
          .catch(() => setAgentInventoryLoadFailed(true));
      }
    } catch {
      setAgentInventoryLoadFailed(true);
    }
  }, [onLoadMoreConversations]);

  useEffect(() => {
    if (
      workspaceMode !== "agent" ||
      !hasMoreConversations ||
      conversationsLoading
    ) {
      return;
    }

    const inventory = agentInventoryScrollRef.current;
    if (!inventory) return;

    const handleInventoryScroll = () => {
      const distanceToEnd =
        inventory.scrollWidth - inventory.clientWidth - inventory.scrollLeft;
      if (distanceToEnd <= 32) requestMoreConversations();
    };

    inventory.addEventListener("scroll", handleInventoryScroll, {
      passive: true,
    });
    return () => inventory.removeEventListener("scroll", handleInventoryScroll);
  }, [
    conversationsLoading,
    hasMoreConversations,
    requestMoreConversations,
    workspaceMode,
  ]);

  useEffect(() => {
    const updateOffsets = () => {
      setHeaderHeight(
        workspaceMode === "agent"
          ? 0
          : (headerRef.current?.getBoundingClientRect().height ?? 0),
      );
      setBannerHeight(bannerRef.current?.getBoundingClientRect().height ?? 0);
      setAgentInventoryHeight(
        workspaceMode === "agent"
          ? (agentInventoryRef.current?.getBoundingClientRect().height ?? 0)
          : 0,
      );
      setStickyTopContentHeight(
        stickyTopContentRef.current?.getBoundingClientRect().height ?? 0,
      );
      const appHeader = document.querySelector("header");
      setUpperbarHeight(
        appHeader instanceof HTMLElement ? appHeader.offsetHeight : 56,
      );
    };

    updateOffsets();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateOffsets);
    if (headerRef.current) observer.observe(headerRef.current);
    if (bannerRef.current) observer.observe(bannerRef.current);
    if (agentInventoryRef.current) observer.observe(agentInventoryRef.current);
    if (stickyTopContentRef.current)
      observer.observe(stickyTopContentRef.current);
    const appHeader = document.querySelector("header");
    if (appHeader instanceof HTMLElement) observer.observe(appHeader);

    return () => observer.disconnect();
  }, [
    chatDisabled,
    currentConversation?.isArchived,
    stickyTopContent,
    workspaceMode,
  ]);

  // Get background jobs from chat store
  const backgroundJobs = useChatStore((state) => state.backgroundJobs);
  const [documentJobBindings, setDocumentJobBindings] = React.useState<
    Record<string, string>
  >({});
  useEffect(
    () =>
      subscribeToConversationSseFrames((frame) => {
        setDocumentJobBindings((current) =>
          recordDocumentJobBinding(current, frame),
        );
      }),
    [],
  );
  const removeBackgroundJob = useChatStore(
    (state) => state.removeBackgroundJob,
  );

  useEffect(() => {
    const updateComposerHeight = () => {
      setComposerHeight(
        composerRef.current?.getBoundingClientRect().height ?? 0,
      );
    };

    updateComposerHeight();

    if (!composerRef.current || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateComposerHeight);
    observer.observe(composerRef.current);

    return () => observer.disconnect();
  }, [
    shouldShowComposer,
    attachedFiles.length,
    images.length,
    backgroundJobs.length,
    fileError,
    editAttachmentRestoreState,
  ]);

  // Subscribe to tool executions in the store to force re-renders when badges change
  // This fixes an issue where React.memo prevents re-renders when tool executions are added
  // IMPORTANT: We use a custom selector that only extracts tool execution data, not message content.
  // This prevents re-renders during content streaming (which happens every 50ms).
  const toolExecutionSignature = useChatStore(
    useCallback((state) => {
      // Only extract tool execution info - ignore content changes to prevent
      // expensive re-renders during streaming
      return state.messages
        .map((m) => {
          const executions = m.toolExecutions || [];
          if (executions.length === 0) return null;
          return `${m.id}:${executions.map((t) => `${t.id}-${t.status}`).join(",")}`;
        })
        .filter(Boolean)
        .join("|");
    }, []),
  );

  // Handle dismissing a completed/failed job
  const handleDismissJob = React.useCallback(
    (jobId: string) => {
      removeBackgroundJob(jobId);
    },
    [removeBackgroundJob],
  );

  // Whether the view should keep following new content. True until the reader
  // scrolls away from the bottom, true again as soon as they come back.
  const isPinnedToBottomRef = useRef(true);

  // Deadline until which a scroll still counts as the reader's own doing.
  // Held in a ref rather than inside the listener effect: that effect
  // re-attaches on every `messages` change, and a local would be zeroed each
  // time, swallowing an unpin whose scroll event had not yet been delivered.
  const humanScrollIntentUntilRef = useRef(0);

  // Content-relative position sampled after a scroll event. Keeping these
  // beside the listener lets an at-bottom browser clamp re-baseline the
  // follow decision before the next streamed chunk arrives.
  const lastFollowAnchorRef = useRef<HTMLElement | null>(null);
  const lastFollowOffsetRef = useRef(0);

  // The scrolling ancestor of the end sentinel. Resolved from the DOM rather
  // than assumed, so a layout change cannot silently break the anchoring.
  const getScrollContainer = React.useCallback((): HTMLElement | null => {
    let element: HTMLElement | null = chatEndRef.current?.parentElement ?? null;
    while (element) {
      const overflowY = window.getComputedStyle(element).overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        element.scrollHeight > element.clientHeight
      ) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }, []);

  // Track whether the user is at the bottom. Reading this from actual scroll
  // events is what lets a deliberate scroll-up survive: previously any new
  // content yanked the view back down, so a user could not read earlier
  // messages while a reply was still arriving.
  // Re-runs on `messages` deliberately. The container is only discoverable
  // once there is enough content to scroll, so resolving it a single time on
  // mount finds nothing, leaves the listener unattached, and strands the flag
  // at its initial true -- which silently restores the old always-scroll
  // behaviour. Re-attaching keeps trying until the container exists.
  useEffect(() => {
    const container = getScrollContainer();
    if (!container) return;

    // A scroll event alone cannot say WHO scrolled: the reader, our own
    // follow-the-stream write, or the browser reacting to new content. And
    // scroll events are delivered asynchronously, so a chunk arriving in the
    // gap after a reader scrolls up but before the event lands would still
    // see the flag pinned and drag them back down.
    //
    // So unpinning requires evidence of human intent -- a wheel, a touch
    // drag, or a navigation key -- within a short window of the scroll.
    // Content growth on its own can never unpin, and landing back at the
    // bottom always re-pins, however it was reached.
    const markHumanIntent = () => {
      humanScrollIntentUntilRef.current =
        Date.now() + HUMAN_SCROLL_INTENT_WINDOW_MS;
    };

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.clientHeight - container.scrollTop;
      const atBottom = distanceFromBottom <= SCROLL_PIN_THRESHOLD_PX;
      if (atBottom) {
        isPinnedToBottomRef.current = true;
        const anchor = (chatEndRef.current?.previousElementSibling ??
          null) as HTMLElement | null;
        lastFollowAnchorRef.current = anchor;
        lastFollowOffsetRef.current = anchor
          ? container.scrollTop - anchor.offsetTop
          : 0;
        return;
      }
      if (Date.now() < humanScrollIntentUntilRef.current) {
        isPinnedToBottomRef.current = false;
      }
    };

    // `pointerdown` is what covers dragging the scrollbar thumb, clicking the
    // scrollbar track, and middle-click autoscroll. None of those emit wheel,
    // touch or key events, so without it a reader who scrolls with the
    // scrollbar is still dragged back to the bottom -- the exact defect this
    // code exists to fix, left in place for mouse users.
    //
    // `keydown` fires here only when focus is inside the list, since the
    // container is not itself focusable; it is kept for that case rather than
    // as general keyboard coverage.
    container.addEventListener("pointerdown", markHumanIntent, {
      passive: true,
    });
    container.addEventListener("wheel", markHumanIntent, { passive: true });
    container.addEventListener("touchmove", markHumanIntent, { passive: true });
    container.addEventListener("keydown", markHumanIntent);
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("pointerdown", markHumanIntent);
      container.removeEventListener("wheel", markHumanIntent);
      container.removeEventListener("touchmove", markHumanIntent);
      container.removeEventListener("keydown", markHumanIntent);
      container.removeEventListener("scroll", handleScroll);
    };
  }, [getScrollContainer, messages]);

  // The pinned flag is a ref on a component that is not keyed by
  // conversation, so without this an unpinned state leaks across a
  // conversation switch: the reader opens a different conversation, lands in
  // the middle of its history, and that conversation's next stream does not
  // follow either. Opening a conversation must always start at the newest
  // message.
  //
  // Done during render rather than in an effect, deliberately. A conversation
  // switch can deliver the new id and the new messages in the SAME commit, and
  // effects run in declaration order, so an effect here would only work while
  // it stayed declared above the follow effect below -- and would become a
  // silent no-op the moment someone reordered them, leaving the reader
  // mid-history with nothing to indicate why. Render precedes every effect, so
  // this cannot be broken by reordering. Comparing against a ref makes it
  // idempotent under a double render.
  const previousConversationIdRef = useRef(currentConversationId);
  if (previousConversationIdRef.current !== currentConversationId) {
    previousConversationIdRef.current = currentConversationId;
    isPinnedToBottomRef.current = true;
    humanScrollIntentUntilRef.current = 0;
  }

  // Follow new content, but only while pinned, and without an animation.
  //
  // A smooth scroll animates for a few hundred milliseconds, while streamed
  // content arrives every ~150ms. Each chunk restarted an animation that
  // could never catch up, so the view fell thousands of pixels behind the
  // bottom during a burst and then lurched down to meet it -- measured at a
  // 4541px gap followed by a 1.5s chase on an eighty-sentence reply. Setting
  // the offset directly tracks growth frame by frame, which is what actually
  // reads as smoothly following.
  // Where the reader was when this effect last looked, measured against the
  // CONTENT rather than against the container's own coordinate system, plus
  // the element it was measured against. Node identity is the "never measured"
  // sentinel: a missing anchor, or a different one, means no comparison is
  // available and the check below is skipped rather than guessed at.
  useEffect(() => {
    const container = getScrollContainer();
    if (!container) {
      chatEndRef.current?.scrollIntoView({ block: "end" });
      return;
    }

    // Decide from the offset itself rather than from the pinned flag, because
    // the scroll event that would have cleared that flag may not have been
    // delivered yet. Under dense streaming the follow effect runs many times
    // inside that gap, so a reader's discrete scroll -- one wheel notch, a
    // scrollbar track click, PageUp -- was being overwritten and then latched:
    // the write moved them to the bottom, handleScroll then saw "at bottom"
    // and re-pinned, and every later commit in the burst held them there.
    //
    // Measure the reader against the CONTENT, not against the container.
    //
    // HOLDS: content changing ABOVE the anchor moves scrollTop and the
    //   anchor's offsetTop by the same amount, so their difference does not
    //   move. Content changing BELOW the anchor moves neither. A scroll is the
    //   only thing that moves the difference, which is what makes it safe to
    //   read a move as the reader's.
    // DOES NOT HOLD: if the anchor node is RECYCLED rather than re-created --
    //   a virtualised list -- identity still matches while its content, and so
    //   its offsetTop, jumps. This list is not virtualised today: every message
    //   row is mounted. If that changes, match on a stable message id instead
    //   of on the node, or this check silently starts lying.
    //
    // WHY NOT RAW scrollTop, since that is what this code used to compare:
    // content collapsing ABOVE the viewport moves scrollTop UP by the
    // collapsed height -- measured at 800px on this container -- which is
    // indistinguishable from a reader. Distance-from-bottom cannot cover that
    // gap, because a chunk appended below raises distance while leaving
    // scrollTop untouched. The two have OPPOSITE blind spots, so requiring
    // both does not close either one: a collapse above followed by an append
    // below satisfies both at once, and unpins a reader who is sitting at the
    // bottom watching the stream. That is a measured sequence, not a
    // hypothetical, and it is what this anchor-relative measurement fixes.
    const anchor = (chatEndRef.current?.previousElementSibling ??
      null) as HTMLElement | null;
    const offsetNow = anchor ? container.scrollTop - anchor.offsetTop : 0;

    if (anchor && anchor === lastFollowAnchorRef.current) {
      const movedUpBy = lastFollowOffsetRef.current - offsetNow;
      const distanceFromBottom =
        container.scrollHeight - container.clientHeight - container.scrollTop;
      if (
        movedUpBy > SCROLL_PIN_THRESHOLD_PX &&
        distanceFromBottom > SCROLL_PIN_THRESHOLD_PX &&
        Date.now() < humanScrollIntentUntilRef.current
      ) {
        isPinnedToBottomRef.current = false;
        lastFollowAnchorRef.current = anchor;
        lastFollowOffsetRef.current = offsetNow;
        return;
      }
    }

    if (!isPinnedToBottomRef.current) {
      // Re-baseline while unpinned as well. Otherwise the reader's deliberate
      // return to the bottom is compared against a position from before they
      // ever moved, and gets discarded as if it were a scroll away.
      lastFollowAnchorRef.current = anchor;
      lastFollowOffsetRef.current = offsetNow;
      return;
    }

    container.scrollTop = container.scrollHeight;
    // Read back rather than reusing scrollHeight: the browser clamps the
    // assignment, and scrollHeight sits one clientHeight above the largest
    // offset the container can actually hold, so a stale unclamped value would
    // make the next comparison read as an upward move.
    lastFollowAnchorRef.current = anchor;
    lastFollowOffsetRef.current = anchor
      ? container.scrollTop - anchor.offsetTop
      : 0;
  }, [messages, getScrollContainer]);

  // Focus input when loading completes (AI finished responding)
  const prevIsLoading = useRef(isLoading);
  useEffect(() => {
    if (prevIsLoading.current && !isLoading) {
      // Loading just finished - focus the input
      textareaRef.current?.focus();
    }
    prevIsLoading.current = isLoading;
  }, [isLoading]);

  // Global ESC key listener to cancel stream (textarea is disabled during loading)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isLoading && !isStopping && onCancelStream) {
        e.preventDefault();
        onCancelStream();
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isLoading, isStopping, onCancelStream]);

  const handleSend = async () => {
    if (
      (!inputValue.trim() && images.length === 0) ||
      isUploading ||
      chatDisabled ||
      conversationSwitching
    )
      return;

    // Sending is an explicit request to see what happens next, so it always
    // returns the reader to the bottom. Without this, someone who scrolled up
    // to re-read something and then sent a message would have their own
    // message and the entire reply render below the viewport, with no sign
    // anything had happened at all.
    isPinnedToBottomRef.current = true;
    if (editAttachmentRestoreState === "restoring") return;
    if (
      editingMessage &&
      editingOriginalAttachmentCount > 0 &&
      editAttachmentRestoreState === "failed"
    ) {
      setFileError(
        "The original attachment could not be restored for editing. Refresh and try again.",
      );
      return;
    }

    const message = inputValue.trim();
    const files = attachedFiles;
    const wasEditing = editingMessage;
    setFileError(null);

    if (isLoading) {
      if (wasEditing) {
        setFileError("Finish editing the message before queueing it.");
        return;
      }
      if (files.length > 0) {
        setFileError(
          "Document attachments cannot be queued while a turn is running.",
        );
        return;
      }
      if (!onQueueMessage) return;
      const imageReferences = images.map((image) => image.reference);
      try {
        await onQueueMessage(message, imageReferences);
        setImageAttemptCount(null);
        setImages([]);
        setInputValue("");
        setAttachedFiles([]);
      } catch {
        // Keep the draft when the server does not acknowledge queue admission.
      }
      return;
    }

    if (images.length > 0) {
      if (wasEditing) {
        setFileError("Finish editing the message before attaching images.");
        return;
      }
      if (files.length > 0) {
        if (!onSendMessageWithFiles) {
          setFileError("Documents cannot be sent in this conversation.");
          return;
        }
        // Mixed send: one multipart message carrying both. The picker's
        // preview URLs are owned by the picker; the host only drops its
        // references and never revokes them.
        const imageReferences = images.map((image) => image.reference);
        setImageAttemptCount(null);
        setImages([]);
        setInputValue("");
        setAttachedFiles([]);
        try {
          await onSendMessageWithFiles(message, files, imageReferences);
        } catch (error) {
          setImages(images);
          setInputValue(message);
          setAttachedFiles(files);
          setFileError(
            error instanceof Error ? error.message : "Unable to send message",
          );
        }
        return;
      }
      const clear = () => {
        setImageAttemptCount(null);
        setImages([]);
        setInputValue("");
      };
      try {
        setImageAttemptCount(messages.length);
        await onSendMessage(
          message,
          images.map((image) => image.reference),
          clear,
        );
      } catch (error) {
        setFileError(
          error instanceof Error ? error.message : "Unable to send images",
        );
      }
      return;
    }

    // Clear input immediately for responsive UI
    setInputValue("");
    setAttachedFiles([]);
    setEditingMessage(null);
    setEditAttachmentRestoreState("idle");
    setEditingOriginalAttachmentCount(0);

    // If editing, delete from the edited message first
    if (wasEditing && onDeleteMessagesFrom) {
      try {
        await onDeleteMessagesFrom(wasEditing);
      } catch (err) {
        console.error("Failed to delete messages for edit:", err);
        // Continue with send anyway
      }
    }

    if (files.length > 0 && onSendMessageWithFiles) {
      try {
        await onSendMessageWithFiles(message, files);
      } catch (error) {
        setInputValue(message);
        setAttachedFiles(files);
        setFileError(
          error instanceof Error ? error.message : "Unable to send message",
        );
      }
    } else {
      try {
        onTextSendStarted?.(message, () =>
          setInputValue((previous) => (previous === "" ? message : previous)),
        );
        await onSendMessage(message);
      } catch (error) {
        setInputValue((previous) => (previous === "" ? message : previous));
        setFileError(
          error instanceof Error ? error.message : "Unable to send message",
        );
      }
    }
  };

  const followUpSuggestionsBlocked =
    isUploading ||
    chatDisabled ||
    conversationSwitching ||
    agentCreationPending ||
    editingMessage !== null;

  const handleFollowUpSuggestionSelect = (suggestion: string) => {
    if (followUpSuggestionsBlocked) return;
    // Same contract as handleSend: sending returns the reader to the bottom.
    isPinnedToBottomRef.current = true;
    if (isLoading) {
      if (!onQueueMessage) return;
      void (async () => {
        try {
          await onQueueMessage(suggestion, []);
        } catch {
          // Keep the suggestion visible if queue admission fails.
        }
      })();
      return;
    }
    onSendMessage(suggestion);
  };

  // Handle edit button click - populate input with message content
  const handleEditMessage = useCallback(
    async (message: ChatMessageType) => {
      const requestId = editRestoreRequestIdRef.current + 1;
      editRestoreRequestIdRef.current = requestId;

      const messageWithFiles = message as ChatMessageWithFiles;
      const originalAttachments = messageWithFiles.fileAttachments || [];

      setInputValue(message.content);
      setEditingMessage(message);
      setFileError(null);
      setAttachedFiles([]);
      setEditingOriginalAttachmentCount(originalAttachments.length);
      setEditAttachmentRestoreState(
        originalAttachments.length > 0 ? "restoring" : "idle",
      );
      textareaRef.current?.focus();

      if (originalAttachments.length === 0 || !onRestoreMessageFiles) {
        return;
      }

      try {
        const restoredFiles = await onRestoreMessageFiles(messageWithFiles);
        if (editRestoreRequestIdRef.current !== requestId) return;
        setAttachedFiles(restoredFiles);
        setEditAttachmentRestoreState("done");
      } catch (err) {
        console.error("Failed to restore message attachments for edit:", err);
        if (editRestoreRequestIdRef.current !== requestId) return;
        setEditAttachmentRestoreState("failed");
        setFileError(
          "The original attachment could not be restored for editing. Refresh and try again.",
        );
      }
    },
    [onRestoreMessageFiles],
  );

  // Handle copy button click - copy message content to clipboard
  const handleCopyMessage = useCallback((message: ChatMessageType) => {
    navigator.clipboard.writeText(message.content).then(() => {
      setCopiedMessageId(message.id);
      setTimeout(() => setCopiedMessageId(null), 1500);
    });
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // ESC is handled by global listener (textarea disabled during loading)
  };

  // Auto-dismiss file error after 5 seconds
  useEffect(() => {
    if (
      !fileError ||
      fileError === "IMAGE_INPUT_UNSUPPORTED" ||
      fileError === "IMAGE_INPUT_DISABLED"
    )
      return;
    const timer = setTimeout(() => setFileError(null), 5000);
    return () => clearTimeout(timer);
  }, [fileError]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearchConversations(query);
  };

  // Begin inline-rename of a conversation in the history sidebar list.
  const handleStartRenameConvo = useCallback(
    (id: number, currentTitle: string | null | undefined) => {
      setRenamingConvoId(id);
      setRenameDraft(currentTitle ?? "");
    },
    [],
  );

  const handleCancelRenameConvo = useCallback(() => {
    setRenamingConvoId(null);
    setRenameDraft("");
  }, []);

  const handleSubmitRenameConvo = useCallback(
    async (id: number) => {
      if (!onRenameConversation) return;
      const trimmed = renameDraft.trim();
      // Reject empty on the FE -- snappier than waiting for the BFF 422.
      if (!trimmed) {
        handleCancelRenameConvo();
        return;
      }
      setRenameSubmitting(true);
      try {
        await onRenameConversation(id, trimmed);
        setRenamingConvoId(null);
        setRenameDraft("");
      } catch {
        // Parent rolled back optimistic state; leave editor open so the
        // user can adjust and retry. Errors surface via the parent's
        // error notifier.
      } finally {
        setRenameSubmitting(false);
      }
    },
    [handleCancelRenameConvo, onRenameConversation, renameDraft],
  );

  // Same lifecycle, separately tracked for the active chat header so the
  // user can edit either surface without the two stepping on each other.
  const handleStartRenameHeader = useCallback(
    (currentTitle: string | null | undefined) => {
      setHeaderRenaming(true);
      setHeaderRenameDraft(currentTitle ?? "");
    },
    [],
  );

  const handleCancelRenameHeader = useCallback(() => {
    setHeaderRenaming(false);
    setHeaderRenameDraft("");
  }, []);

  const handleSubmitRenameHeader = useCallback(async () => {
    if (!onRenameConversation || currentConversationId === null) return;
    const trimmed = headerRenameDraft.trim();
    if (!trimmed) {
      handleCancelRenameHeader();
      return;
    }
    const idAsNumber =
      typeof currentConversationId === "number"
        ? currentConversationId
        : Number(currentConversationId);
    if (Number.isNaN(idAsNumber)) return;
    setRenameSubmitting(true);
    try {
      await onRenameConversation(idAsNumber, trimmed);
      setHeaderRenaming(false);
      setHeaderRenameDraft("");
    } catch {
      // Parent rolled back; leave editor open.
    } finally {
      setRenameSubmitting(false);
    }
  }, [
    currentConversationId,
    handleCancelRenameHeader,
    headerRenameDraft,
    onRenameConversation,
  ]);

  const getAgentDisplayName = useCallback(
    (conversation: CollaborationConversationSummary) =>
      conversation.agentName ||
      conversation.title ||
      `${modeLabels.rowFallback} ${conversation.id}`,
    [modeLabels.rowFallback],
  );
  const currentDisplayTitle = useMemo(() => {
    if (!currentConversation) return title || modeLabels.headerFallbackTitle;
    if (workspaceMode === "agent")
      return getAgentDisplayName(currentConversation);
    return currentConversation.title || title || modeLabels.headerFallbackTitle;
  }, [
    currentConversation,
    getAgentDisplayName,
    modeLabels.headerFallbackTitle,
    title,
    workspaceMode,
  ]);

  const handleOpenAgentMenu = useCallback(
    (
      event: React.SyntheticEvent<HTMLElement>,
      conversation: CollaborationConversationSummary,
    ) => {
      event.stopPropagation();
      if (
        !canShowAgentActionMenu(workspaceRole) ||
        (agentMenuAnchorRef.current &&
          agentMenuConversationIdRef.current === conversation.id)
      ) {
        setAgentMenuAnchor(null);
        setAgentMenuConversation(null);
        return;
      }
      setAgentMenuAnchor(event.currentTarget);
      setAgentMenuConversation(conversation);
    },
    [workspaceRole],
  );
  const handleSelectAgent = useCallback(
    (id: number) => {
      onLoadConversation(id);
    },
    [onLoadConversation],
  );

  const handleCloseAgentMenu = useCallback(() => {
    setAgentMenuAnchor(null);
    setAgentMenuConversation(null);
  }, []);

  const refreshAvatarReservations = useCallback(
    async (conversation: CollaborationConversationSummary) => {
      if (!onGetAgentAvatarReservations) {
        setAvatarReservations(null);
        setAvatarAvailabilityError(
          "Appearance availability could not be loaded.",
        );
        return false;
      }
      setAvatarAvailabilityLoading(true);
      setAvatarAvailabilityError(null);
      try {
        const reservations = await onGetAgentAvatarReservations(
          conversation.id,
        );
        const cuts = AGENT_AVATAR_CUTS.filter(
          (cut) => !reservations.reservedCuts.includes(cut),
        );
        const colours = AGENT_AVATAR_COLOURS.filter(
          (colour) => !reservations.reservedColours.includes(colour),
        );
        const display = resolveAgentAvatarDisplay(conversation.avatar);
        setAvatarReservations(reservations);
        setAvatarPickerCut(
          display.cut && cuts.includes(display.cut)
            ? display.cut
            : (cuts[0] ?? null),
        );
        setAvatarPickerColour(
          display.colour && colours.includes(display.colour)
            ? display.colour
            : (colours[0] ?? null),
        );
        return true;
      } catch {
        setAvatarReservations(null);
        setAvatarAvailabilityError(
          "Appearance availability could not be loaded.",
        );
        return false;
      } finally {
        setAvatarAvailabilityLoading(false);
      }
    },
    [onGetAgentAvatarReservations],
  );

  const handleOpenAvatarPicker = useCallback(
    (conversation: CollaborationConversationSummary) => {
      setAvatarPickerConversation(conversation);
      setAvatarPickerCut(null);
      setAvatarPickerColour(null);
      setAvatarReservations(null);
      setAvatarActionError(null);
      setAvatarAvailabilityError(null);
      handleCloseAgentMenu();
      void refreshAvatarReservations(conversation);
    },
    [handleCloseAgentMenu, refreshAvatarReservations],
  );

  const handleAssignAvatar = useCallback(async () => {
    if (
      !avatarPickerConversation ||
      !onAssignAgentAvatar ||
      !avatarPickerCut ||
      !avatarPickerColour
    )
      return;
    setAvatarActionSaving(true);
    setAvatarActionError(null);
    try {
      await onAssignAgentAvatar(avatarPickerConversation.id, {
        selected_cut: avatarPickerCut,
        selected_colour: avatarPickerColour,
      });
      // This refresh is informational after a successful PUT.  It must not
      // turn a persisted success into a failed picker action.
      await refreshAvatarReservations(avatarPickerConversation);
      setAvatarPickerConversation(null);
    } catch (error) {
      const typedError = error as { status?: number; message?: string };
      if (typedError.status === 409) {
        setAvatarActionError(
          /busy/i.test(typedError.message || "")
            ? "This agent appearance is being updated. Try again."
            : "That cut or colour is unavailable. Choose another pair.",
        );
      } else {
        setAvatarActionError(
          "Appearance could not be assigned. The list was refreshed.",
        );
      }
      if (typedError.status === 409) {
        await refreshAvatarReservations(avatarPickerConversation);
      }
    } finally {
      setAvatarActionSaving(false);
    }
  }, [
    avatarPickerColour,
    avatarPickerConversation,
    avatarPickerCut,
    onAssignAgentAvatar,
    refreshAvatarReservations,
  ]);

  const handleReleaseAvatar = useCallback(
    async (conversation: CollaborationConversationSummary) => {
      if (!onReleaseAgentAvatar) return;
      setAvatarActionSaving(true);
      setAvatarActionError(null);
      try {
        await onReleaseAgentAvatar(conversation.id);
      } catch {
        setAvatarActionError(
          "Appearance could not be released. The list was refreshed.",
        );
      } finally {
        setAvatarActionSaving(false);
        handleCloseAgentMenu();
      }
    },
    [handleCloseAgentMenu, onReleaseAgentAvatar],
  );

  const handleOpenPersonaModal = useCallback(
    (conversation: CollaborationConversationSummary) => {
      setPersonaConversation(conversation);
      setPersonaNameDraft(getAgentDisplayName(conversation));
      setPersonaPromptDraft(conversation.systemPromptOverride || "");
      setPersonaError(null);
      setPersonaLibraryName(getAgentDisplayName(conversation));
      setPersonaEditing(null);
      setPersonaLibraryError(null);
      if (!workspaceId) {
        setPersonaLibraryError("Reusable personas need an active workspace.");
        handleCloseAgentMenu();
        return;
      }
      setPersonaLibraryLoading(true);
      void personas
        .list(workspaceId, conversation.id)
        .then(setPersonaLibrary)
        .catch(() =>
          setPersonaLibraryError("Reusable personas could not be loaded."),
        )
        .finally(() => setPersonaLibraryLoading(false));
      handleCloseAgentMenu();
    },
    [getAgentDisplayName, handleCloseAgentMenu, personas, workspaceId],
  );

  const handleSubmitPersona = useCallback(async () => {
    if (!personaConversation || !onUpdateAgentConversation) return;
    const nextName = personaNameDraft.trim();
    if (!nextName) return;
    setPersonaSubmitting(true);
    setPersonaError(null);
    try {
      await onUpdateAgentConversation(personaConversation.id, {
        agentName: nextName,
        systemPromptOverride: personaPromptDraft.trim() || null,
      });
      setPersonaConversation(null);
    } catch (error) {
      setPersonaError(
        error instanceof Error
          ? error.message
          : "Could not update agent details.",
      );
    } finally {
      setPersonaSubmitting(false);
    }
  }, [
    onUpdateAgentConversation,
    personaConversation,
    personaNameDraft,
    personaPromptDraft,
  ]);

  const handleApplyLibraryPersona = useCallback(
    async (personaId: string) => {
      if (!personaConversation || !onUpdateAgentConversation || !workspaceId)
        return;
      const selected = [...personaLibrary.own, ...personaLibrary.shared].find(
        (persona) => persona.id === personaId,
      );
      if (!selected) return;
      setPersonaSubmitting(true);
      setPersonaLibraryError(null);
      try {
        await onUpdateAgentConversation(personaConversation.id, {
          personaSourceId: personaId,
        });
        // The apply route copies a library persona by value. Reflect that copy
        // immediately in this editor, then refetch to obtain its authoritative
        // source/version update marker for a later Reload action.
        setPersonaNameDraft(selected.name);
        setPersonaPromptDraft(selected.body);
        const groups = await personas.list(workspaceId, personaConversation.id);
        setPersonaLibrary(groups);
      } catch {
        // Both the copy-by-value PATCH and the authoritative Reload lookup are
        // required for a truthful library state. Do not leave a void-invoked
        // rejection unhandled or silently claim that the persona was applied.
        setPersonaLibraryError(
          "Reusable persona could not be applied. Review the agent and try again.",
        );
      } finally {
        setPersonaSubmitting(false);
      }
    },
    [
      onUpdateAgentConversation,
      personaConversation,
      personaLibrary,
      personas,
      workspaceId,
    ],
  );

  const handleSaveLibraryPersona = useCallback(async () => {
    const name = personaLibraryName.trim();
    const body = personaPromptDraft.trim();
    if (!name || !body || !workspaceId) return;
    setPersonaSubmitting(true);
    try {
      await personas.create(workspaceId, {
        name,
        body,
        is_shared: personaLibraryShared,
      });
      if (personaConversation)
        setPersonaLibrary(
          await personas.list(workspaceId, personaConversation.id),
        );
    } finally {
      setPersonaSubmitting(false);
    }
  }, [
    personaConversation,
    personaLibraryName,
    personaLibraryShared,
    personaPromptDraft,
    personas,
    workspaceId,
  ]);

  const refreshPersonaLibrary = useCallback(async () => {
    if (!workspaceId || !personaConversation) return;
    setPersonaLibrary(await personas.list(workspaceId, personaConversation.id));
  }, [personaConversation, personas, workspaceId]);

  const handleBeginPersonaEdit = useCallback((persona: ReusablePersona) => {
    setPersonaEditing(persona);
    setPersonaEditName(persona.name);
    setPersonaEditBody(persona.body);
    setPersonaEditShared(persona.is_shared);
    setPersonaLibraryError(null);
  }, []);

  const handleUpdateLibraryPersona = useCallback(async () => {
    if (!workspaceId || !personaEditing) return;
    const name = personaEditName.trim();
    const body = personaEditBody.trim();
    if (!name || !body) return;
    setPersonaSubmitting(true);
    setPersonaLibraryError(null);
    try {
      await personas.update(workspaceId, personaEditing.id, {
        name,
        body,
        is_shared: personaEditShared,
      });
      await refreshPersonaLibrary();
      setPersonaEditing(null);
    } catch {
      setPersonaLibraryError("Reusable persona could not be updated.");
    } finally {
      setPersonaSubmitting(false);
    }
  }, [
    personaEditBody,
    personaEditName,
    personaEditShared,
    personaEditing,
    personas,
    refreshPersonaLibrary,
    workspaceId,
  ]);

  const handleTogglePersonaSharing = useCallback(
    async (persona: ReusablePersona, isShared: boolean) => {
      if (!workspaceId) return;
      setPersonaSubmitting(true);
      setPersonaLibraryError(null);
      try {
        await personas.update(workspaceId, persona.id, {
          name: persona.name,
          body: persona.body,
          is_shared: isShared,
        });
        await refreshPersonaLibrary();
      } catch {
        setPersonaLibraryError(
          "Reusable persona sharing could not be updated.",
        );
      } finally {
        setPersonaSubmitting(false);
      }
    },
    [personas, refreshPersonaLibrary, workspaceId],
  );

  const handleDeleteLibraryPersona = useCallback(
    async (persona: ReusablePersona) => {
      if (!workspaceId) return;
      setPersonaSubmitting(true);
      setPersonaLibraryError(null);
      try {
        await personas.remove(workspaceId, persona.id);
        await refreshPersonaLibrary();
        if (personaEditing?.id === persona.id) setPersonaEditing(null);
      } catch {
        setPersonaLibraryError("Reusable persona could not be deleted.");
      } finally {
        setPersonaSubmitting(false);
      }
    },
    [personaEditing?.id, personas, refreshPersonaLibrary, workspaceId],
  );

  const handleAddCollaborators = useCallback(
    async (userIds: string[]) => {
      if (!onAddCollaborators) return;
      setCollaborationSaving(true);
      try {
        await onAddCollaborators(userIds);
        setCollaboratorModalOpen(false);
        setCollaborationNotice(
          userIds.length === 1
            ? "Collaborator added"
            : `${userIds.length} collaborators added`,
        );
      } finally {
        setCollaborationSaving(false);
      }
    },
    [onAddCollaborators],
  );

  const handleRemoveCollaborator = useCallback(
    async (userId: string) => {
      if (!onRemoveCollaborator) return;
      setCollaborationSaving(true);
      try {
        await onRemoveCollaborator(userId);
        const removed =
          currentConversationCollaborators.find(
            (collaborator) => collaborator.user_id === userId,
          )?.display_name || "Collaborator";
        setCollaborationNotice(`${removed} removed`);
      } finally {
        setCollaborationSaving(false);
      }
    },
    [currentConversationCollaborators, onRemoveCollaborator],
  );

  const buildFallbackAttributions = useCallback(
    (message: ChatMessageType): SkillInvocationAttribution[] => {
      const readResultObject = (
        value: unknown,
      ): Record<string, unknown> | null =>
        value && typeof value === "object" && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : null;
      const executions = message.toolExecutions || [];
      return executions.map((execution, index) => ({
        ...(() => {
          const resultObject = readResultObject(execution.result);
          const userDisplay = readResultObject(resultObject?.user_display);
          const isBackgroundJob =
            resultObject?.background === true ||
            typeof resultObject?.job_id === "string";
          const resultSuccess =
            typeof resultObject?.success === "boolean"
              ? Boolean(resultObject.success)
              : execution.status !== "error";
          const resultError = isBackgroundJob
            ? null
            : execution.error ||
              (typeof resultObject?.error === "string"
                ? resultObject.error
                : null);
          const outputSummary = isBackgroundJob
            ? null
            : typeof userDisplay?.summary === "string"
              ? userDisplay.summary
              : typeof resultObject?.error === "string"
                ? resultObject.error
                : resultObject
                  ? JSON.stringify(resultObject)
                  : typeof execution.result === "string"
                    ? execution.result
                    : null;
          return {
            success: resultSuccess,
            error: resultError,
            output_summary: outputSummary,
            result_status:
              !isBackgroundJob && typeof userDisplay?.status === "string"
                ? userDisplay.status
                : null,
            error_code:
              typeof resultObject?.error_code === "string"
                ? resultObject.error_code
                : null,
          };
        })(),
        id: index + 1,
        conversation_id:
          typeof currentConversationId === "number"
            ? currentConversationId
            : Number(currentConversationId ?? 0) || null,
        turn_id:
          typeof (message as any).hubMessageId === "number"
            ? (message as any).hubMessageId
            : typeof message.id === "number"
              ? message.id
              : null,
        tool_call_id: execution.id,
        skill_name: execution.name,
        started_at: new Date().toISOString(),
        duration_ms: null,
        input_args: (execution.args as Record<string, unknown> | null) || null,
      }));
    },
    [currentConversationId],
  );

  // Extension points for ChatMessage - render usage badge for assistant messages
  // and file attachments for user messages
  // Memoize to prevent re-renders of all ChatMessage components on every keystroke
  const messageExtensions: ChatMessageExtensions | undefined = useMemo(
    () => ({
      renderHeader: (message) => {
        const sourceRole =
          (message as PresentationMessage).sourceRole ?? message.role;
        const senderUserId = getTrustedSenderUserId(message as IDAChatMessage);
        const senderAgentId =
          typeof senderUserId === "string"
            ? /^agent:(\d+)$/.exec(senderUserId)
            : null;
        const agentPresentation = resolveAgentMessagePresentation(
          message as IDAChatMessage,
        );
        const displayedAgent = agentPresentation?.agent;
        const agentBubbleTone = agentPresentation?.bubbleTone;
        if (sourceRole === "user" || senderAgentId || displayedAgent) {
          const msgWithFiles = message as ChatMessageWithFiles;
          const fileAttachments = msgWithFiles.fileAttachments || [];
          const speaker =
            collaborationEnabled && !senderAgentId
              ? resolveSpeakerDisplayInfo({
                  senderUserId,
                  currentUser,
                  collaborators: currentConversationCollaborators,
                  workspaceUsers,
                  conversations,
                })
              : null;
          const hasAttachments =
            sourceRole === "user" && fileAttachments.length > 0;

          if (
            !speaker &&
            !displayedAgent &&
            !senderAgentId &&
            !hasAttachments
          ) {
            return null;
          }

          return (
            <Stack
              spacing={0.5}
              data-agent-bubble-colour={agentBubbleTone?.colour}
              data-testid={
                agentBubbleTone
                  ? `agent-message-bubble-tone-${message.id}`
                  : undefined
              }
              sx={{
                mb: 0.5,
                ...(agentBubbleTone
                  ? {
                      "& + .mmagent-chat-message__content": {
                        backgroundColor: agentBubbleTone.backgroundColor,
                        color: agentBubbleTone.color,
                      },
                    }
                  : {}),
              }}
            >
              {speaker && (
                <Stack
                  data-testid={`speaker-label-${message.id}`}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    minWidth: 0,
                    alignSelf:
                      speaker.userId === currentUser?.id
                        ? "flex-end"
                        : "flex-start",
                  }}
                >
                  <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                    {speaker.label}
                  </Typography>
                  {speaker.isOwner && (
                    <Chip size="sm" variant="soft" color="neutral">
                      owner
                    </Chip>
                  )}
                </Stack>
              )}
              {displayedAgent && (
                <Stack
                  data-testid={`agent-message-attribution-${message.id}`}
                  direction="row"
                  alignItems="center"
                  sx={{ alignSelf: "flex-start", minHeight: 24 }}
                >
                  <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                    {displayedAgent.agentName ||
                      displayedAgent.title ||
                      "Agent"}
                  </Typography>
                </Stack>
              )}
              {senderAgentId && !displayedAgent && (
                <Typography
                  data-testid={`agent-message-missing-attribution-${message.id}`}
                  level="body-xs"
                  sx={{ color: "text.tertiary", fontWeight: 600 }}
                >
                  {`Agent ${senderAgentId[1]} unavailable`}
                </Typography>
              )}
              {hasAttachments && (
                <Box>
                  <FilePreviewChips
                    files={fileAttachments.map((f) => ({
                      filename: f.filename,
                      fileSize: f.fileSize,
                      mimeType: f.mimeType,
                    }))}
                    size="sm"
                  />
                </Box>
              )}
            </Stack>
          );
        }

        // Show usage badge for assistant messages
        if (
          sourceRole === "assistant" &&
          fetchMessageUsage &&
          currentConversationId
        ) {
          const assistantMessage = message as IDAChatMessage;
          const provenance =
            assistantMessage.metadata?._ida_llm_route_provenance;
          const isVoiceMessage = assistantMessage.metadata?.source === "voice";
          const hasVoiceTranscriptTiming =
            typeof assistantMessage.metadata?.start_ms === "number" &&
            typeof assistantMessage.metadata?.end_ms === "number";
          const voiceModelLabel = !isVoiceMessage
            ? undefined
            : typeof assistantMessage.model === "string"
              ? normalizeVoiceModelLabel(assistantMessage.model)
              : typeof assistantMessage.metadata?.model === "string"
                ? normalizeVoiceModelLabel(assistantMessage.metadata.model)
                : hasVoiceTranscriptTiming ||
                    typeof assistantMessage.metadata?.gpt_live_batch_id ===
                      "string"
                  ? "GPT-Live"
                  : undefined;
          const showRouteProvenance = !isVoiceMessage || !voiceModelLabel;
          if (isVoiceMessage && !provenance && !voiceModelLabel) return null;

          // Grouped assistant bubbles may span multiple Hub rows. For usage,
          // prefer the latest underlying assistant row, since that is where the
          // post-tool summary usage is attached.
          const hubIds = (message as any).hubMessageIds as number[] | undefined;
          const hubId =
            hubIds && hubIds.length > 0
              ? hubIds[hubIds.length - 1]
              : (message as any).hubMessageId;
          const messageId =
            hubId ??
            (typeof message.id === "number"
              ? message.id
              : parseInt(message.id, 10));
          const hasUsageMessageId =
            Number.isSafeInteger(messageId) &&
            messageId >= 0 &&
            messageId <= 1_000_000_000;
          if (!hasUsageMessageId && !isVoiceMessage) return null;

          const convId =
            typeof currentConversationId === "number"
              ? currentConversationId
              : parseInt(currentConversationId as string, 10);
          if (isNaN(convId)) return null;

          const attributions = resolveDisplayedAttributions(
            skillAttributionsByTurnId[messageId] || [],
            buildFallbackAttributions(message),
          );

          return (
            <Stack
              spacing={0.5}
              sx={{ alignItems: "flex-start", mb: 0.5, width: "100%" }}
            >
              <SkillInvocationAttributionCard
                attributions={attributions}
                enabled={skillAttributionEnabled}
              />
              <Box
                data-testid="assistant-message-meta-row"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {showRouteProvenance && (
                  <LlmRouteProvenanceChip inline provenance={provenance} />
                )}
                {isVoiceMessage && voiceModelLabel && (
                  <Chip size="sm" variant="soft" color="neutral">
                    {voiceModelLabel}
                  </Chip>
                )}
                <UsageInfoBadge
                  usage={extractMessageUsage(assistantMessage)}
                  conversationId={convId}
                  messageId={hasUsageMessageId ? messageId : undefined}
                  fetchUsage={hasUsageMessageId ? fetchMessageUsage : undefined}
                />
              </Box>
            </Stack>
          );
        }

        return null;
      },
      // Wrap assistant message content to add image hover overlays
      renderContent: (message, defaultContent) => {
        const sourceRole =
          (message as PresentationMessage).sourceRole ?? message.role;
        const senderUserId = getTrustedSenderUserId(message as IDAChatMessage);
        if (
          sourceRole !== "assistant" ||
          /^agent:\d+$/.test(senderUserId || "")
        ) {
          return defaultContent;
        }
        return <ImageOverlayWrapper>{defaultContent}</ImageOverlayWrapper>;
      },
      // Render copy/edit/delete action buttons on hover
      renderActions: (message: ChatMessageType) => (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            opacity: 0.6,
            "&:hover": { opacity: 1 },
          }}
        >
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={() => handleCopyMessage(message)}
            title="Copy message"
          >
            {copiedMessageId === message.id ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
          {message.role === "user" && onDeleteMessagesFrom && (
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => handleEditMessage(message)}
              title="Edit message"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onDeleteMessagesFrom && (
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              onClick={() => setDeleteMessageConfirm(message)}
              title="Delete message"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    }),
    [
      fetchMessageUsage,
      currentConversationId,
      onDeleteMessagesFrom,
      handleEditMessage,
      handleCopyMessage,
      copiedMessageId,
      buildFallbackAttributions,
      skillAttributionEnabled,
      skillAttributionsByTurnId,
      collaborationEnabled,
      currentUser,
      currentConversationCollaborators,
      workspaceUsers,
      conversations,
      resolveAgentMessagePresentation,
    ],
  );

  // Render image references returned by tools through the authenticated chat route.
  const toolExtensions: ChatToolExecutionExtensions | undefined = useMemo(
    () => ({
      renderResult: (execution: ToolExecution) => {
        const imageRefs = buildToolResultImages(
          execution,
          getToolResultConversationId(
            activeConversationIdNumber,
            visibleMessagesConversationId ?? null,
          ),
        );
        if (imageRefs) return imageRefs;

        const backgroundResult = execution.result as
          Record<string, unknown> | undefined;
        const isBackgroundDocumentTool =
          execution.name === "generate_document" ||
          execution.name === "generate_presentation";
        const documentJob = isBackgroundDocumentTool
          ? getDocumentJobForExecution(
              execution,
              currentConversationId,
              documentJobBindings,
              backgroundJobs,
            )
          : undefined;
        if (
          isBackgroundDocumentTool &&
          (backgroundResult?.background === true || documentJob)
        ) {
          const jobResult = documentJob?.result as
            Record<string, unknown> | undefined;
          return (
            <BackgroundDocumentCompletion
              workspaceId={workspaceId ?? ""}
              status={
                documentJob?.status ??
                (typeof backgroundResult?.job_id === "string"
                  ? "completed"
                  : "running")
              }
              documentId={
                nonEmptyDocumentString(backgroundResult?.document_id) ??
                nonEmptyDocumentString(jobResult?.document_id)
              }
              documentTitle={
                nonEmptyDocumentString(backgroundResult?.document_title) ??
                nonEmptyDocumentString(jobResult?.document_title)
              }
              error={documentJob?.error}
              recoveryMessage={
                documentJob?.recoveryExhausted
                  ? DOCUMENT_JOB_CHECK_BACK_MESSAGE
                  : undefined
              }
            />
          );
        }

        // Other background jobs remain status-only; the assistant continuation
        // carries their visible result.
        if (
          execution.result?.background === true ||
          typeof execution.result?.job_id === "string"
        ) {
          return null;
        }

        if (
          ["generate_image", "visualize_data"].includes(execution.name) &&
          execution.result?.success
        ) {
          const ui = execution.result._ui as
            Record<string, unknown> | undefined;
          const result = execution.result.result as Record<string, unknown>;
          // New format: _ui.image_url, old format: result.sas_url
          const sasUrl = (ui?.image_url || result?.sas_url) as string;
          const filename = (result?.filename as string) || "Generated image";
          if (sasUrl) {
            return <ToolResultImage src={sasUrl} alt={filename} />;
          }
        }
        return null;
      },
    }),
    [
      activeConversationIdNumber,
      backgroundJobs,
      currentConversationId,
      documentJobBindings,
      visibleMessagesConversationId,
      workspaceId,
    ],
  );

  const heartbeatOverlayPortal =
    stickyTopContent && portalTarget
      ? createPortal(
          <Box
            data-testid="chat-heartbeat-overlay"
            sx={{
              position: "fixed",
              top: `${upperbarHeight + headerHeight + bannerHeight + agentInventoryHeight + 12}px`,
              left: "var(--workspace-sidebar-width, 0px)",
              right: 0,
              zIndex: 4,
              px: 2,
              pointerEvents: "none",
            }}
          >
            <Box
              ref={stickyTopContentRef}
              sx={{
                maxWidth: 900,
                mx: "auto",
                display: "flex",
                justifyContent: "flex-end",
                pointerEvents: "auto",
              }}
            >
              {stickyTopContent}
            </Box>
          </Box>,
          portalTarget,
        )
      : null;

  const headerPortal =
    workspaceMode !== "agent" && portalTarget
      ? createPortal(
          <Box
            ref={headerRef}
            data-testid="chat-header-container"
            sx={{
              p: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1,
              position: "fixed",
              top: `${upperbarHeight}px`,
              left: "var(--workspace-sidebar-width, 0px)",
              right: 0,
              zIndex: 3,
              bgcolor: "background.surface",
            }}
          >
            <Tooltip
              title={modeLabels.historyTooltip}
              placement="bottom"
              arrow
              open={historyTooltipOpen}
              onOpen={() => setHistoryTooltipOpen(true)}
              onClose={() => setHistoryTooltipOpen(false)}
            >
              <IconButton
                variant="plain"
                color="neutral"
                onClick={() => {
                  setHistoryOpen(true);
                  setHistoryTooltipOpen(false);
                  try {
                    localStorage.setItem(
                      IDA_HISTORY_PILL_KEY,
                      String(HISTORY_PILL_MAX_SHOWS),
                    );
                  } catch {}
                }}
                data-testid="conversation-history-button"
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            {headerRenaming && onRenameConversation ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Input
                  size="sm"
                  value={headerRenameDraft}
                  autoFocus
                  disabled={renameSubmitting}
                  onChange={(e) => setHeaderRenameDraft(e.target.value)}
                  onBlur={() => {
                    void handleSubmitRenameHeader();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleSubmitRenameHeader();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      handleCancelRenameHeader();
                    }
                  }}
                  slotProps={{ input: { maxLength: 255 } }}
                  placeholder="Press Enter to save, Esc to cancel"
                  data-testid="ida-chat-header-rename-input"
                />
              </Box>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ flex: 1, minWidth: 0 }}
                className="ida-chat-header-title"
              >
                <Typography
                  level="title-md"
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {currentDisplayTitle}
                </Typography>
                {onRenameConversation && currentConversationId !== null && (
                  <Tooltip title={modeLabels.renameTooltip}>
                    <IconButton
                      aria-label={modeLabels.renameAriaLabel}
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() =>
                        handleStartRenameHeader(currentConversation?.title)
                      }
                      data-testid="ida-chat-header-rename-button"
                      sx={{
                        // Pencil is faint until the header is hovered so it
                        // doesn't compete visually with the title text.
                        opacity: 0,
                        transition: "opacity 120ms",
                        ".ida-chat-header-title:hover &": { opacity: 1 },
                        "&:focus-visible": { opacity: 1 },
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
            {onHelpClick && (
              <Tooltip title="Get help">
                <IconButton
                  variant="plain"
                  color="neutral"
                  onClick={onHelpClick}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            )}
            {collaborationEnabled &&
              canManageCollaborators &&
              hasOtherAcceptedWorkspaceMembers &&
              currentConversationId !== null && (
                <Tooltip title="Manage collaborators">
                  <Button
                    variant="plain"
                    color="neutral"
                    onClick={() => setCollaboratorModalOpen(true)}
                    disabled={conversationSwitching}
                    startDecorator={<GroupOutlinedIcon />}
                    size="sm"
                    data-testid="collaborate-button"
                  >
                    Collaborate
                  </Button>
                </Tooltip>
              )}
            <Tooltip title={modeLabels.newTooltip}>
              <Button
                variant="plain"
                color="primary"
                onClick={onNewConversation}
                disabled={conversationSwitching || agentCreationPending}
                startDecorator={<AddIcon />}
                size="sm"
              >
                {modeLabels.newButton}
              </Button>
            </Tooltip>
          </Box>,
          portalTarget,
        )
      : null;

  return (
    <>
      {heartbeatOverlayPortal}
      {headerPortal}
      <Box
        data-testid="chat-layout-shell"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Header with history toggle */}
        <Box
          sx={{
            flexShrink: 0,
            minHeight: headerHeight > 0 ? `${headerHeight}px` : undefined,
          }}
        />
        <Box ref={bannerRef} sx={{ flexShrink: 0 }}>
          {/* Quota / disabled banner */}
          {chatDisabled && (
            <Alert
              variant="soft"
              color="warning"
              sx={{ borderRadius: 0, borderBottom: 1, borderColor: "divider" }}
            >
              {chatDisabledMessage}
            </Alert>
          )}

          {currentConversation?.isArchived && (
            <Alert
              variant="soft"
              color="warning"
              sx={{ borderRadius: 0, borderBottom: 1, borderColor: "divider" }}
            >
              {currentConversation.archivedReason === "owner_left_workspace"
                ? "This chat is archived; owner no longer in workspace."
                : "This chat is archived."}
            </Alert>
          )}
        </Box>

        {workspaceMode === "agent" && (
          <Box
            ref={agentInventoryRef}
            data-testid="agent-inventory-bar"
            data-agent-status-push-active={
              agentStatusPushActive === null
                ? "unknown"
                : String(agentStatusPushActive)
            }
            sx={{
              flexShrink: 0,
              px: 1.25,
              py: 0.5,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.surface",
            }}
          >
            <Stack
              ref={agentInventoryScrollRef}
              data-testid="agent-inventory-scroll"
              direction="row"
              spacing={0.25}
              alignItems="center"
              sx={{
                minWidth: 0,
                overflowX: "auto",
                overflowY: "hidden",
                py: 0.25,
                pr: 0.5,
                scrollbarGutter: "stable",
              }}
            >
              {needsOnboarding && onboardingAgentId !== null && (
                <Button
                  size="sm"
                  variant="soft"
                  data-testid="continue-workspace-setup"
                  disabled={conversationSwitching}
                  onClick={() => handleSelectAgent(onboardingAgentId)}
                >
                  Continue workspace setup
                </Button>
              )}
              {agentConversations.map((conversation) => (
                <AgentInventoryTile
                  key={conversation.id}
                  conversation={conversation}
                  canAccessActions={canShowAgentActionMenu(workspaceRole)}
                  displayName={getAgentDisplayName(conversation)}
                  selected={conversation.id === activeConversationIdNumber}
                  isMenuOpen={agentMenuConversation?.id === conversation.id}
                  isCoarsePointer={isCoarsePointer}
                  disabled={conversationSwitching}
                  activityNow={agentActivityNow}
                  onSelect={handleSelectAgent}
                  onOpenMenu={handleOpenAgentMenu}
                />
              ))}
              {agentInventoryLoadFailed && hasMoreConversations && (
                <Button
                  size="sm"
                  variant="soft"
                  color="danger"
                  data-testid="agent-inventory-load-more-error"
                  onClick={requestMoreConversations}
                  sx={{ flex: "0 0 auto" }}
                >
                  Couldn't load more agents. Retry
                </Button>
              )}
              {canShowAgentActionMenu(workspaceRole) && (
                <Tooltip title="New Agent">
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={onNewConversation}
                    disabled={conversationSwitching || agentCreationPending}
                    aria-label="New Agent"
                    data-testid="agent-inventory-new-agent"
                    sx={{ flex: "0 0 auto", ml: 0.5 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
        )}

        <Menu
          anchorEl={agentMenuAnchor}
          open={Boolean(
            agentMenuAnchor &&
            agentMenuConversation &&
            conversations.some(
              (conversation) =>
                conversation.id === agentMenuConversation.id &&
                canShowAgentActionMenu(workspaceRole),
            ),
          )}
          onClose={handleCloseAgentMenu}
          placement="bottom-start"
        >
          {agentMenuConversation && (
            <>
              {canShowAgentActionMenu(workspaceRole) &&
                !agentMenuConversation.decommissionedAt && (
                  <MenuItem
                    disabled={!onAssignAgentAvatar || conversationSwitching}
                    onClick={() =>
                      handleOpenAvatarPicker(agentMenuConversation)
                    }
                  >
                    Change appearance
                  </MenuItem>
                )}
              {canShowAgentActionMenu(workspaceRole) &&
                Boolean(agentMenuConversation.decommissionedAt) && (
                  <MenuItem
                    disabled={
                      !onReleaseAgentAvatar ||
                      avatarActionSaving ||
                      conversationSwitching
                    }
                    onClick={() =>
                      void handleReleaseAvatar(agentMenuConversation)
                    }
                  >
                    Release appearance
                  </MenuItem>
                )}
              {canShowAgentActionMenu(workspaceRole) && (
                <MenuItem
                  onClick={() => handleOpenPersonaModal(agentMenuConversation)}
                  disabled={!onUpdateAgentConversation || conversationSwitching}
                >
                  Edit persona
                </MenuItem>
              )}
              <MenuItem
                color="danger"
                disabled={!onDeleteConversation || conversationSwitching}
                onClick={() => {
                  setDeleteConfirmId(agentMenuConversation.id);
                  setDeleteConversationError(null);
                  handleCloseAgentMenu();
                }}
              >
                Delete agent
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Messages area.
          Own the scrolling here so the composer can stay pinned to the bottom
          of the viewport-sized chat shell while history scrolls independently. */}
        <Box
          data-testid="chat-scroll-container"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pt:
              stickyTopContentHeight > 0
                ? `${stickyTopContentHeight + 12}px`
                : undefined,
            pb: composerHeight > 0 ? `${composerHeight + 12}px` : 0,
          }}
        >
          {workspaceMode === "agent" &&
          !currentChatAgent &&
          !isLoading &&
          !agentCreationPending ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: "100%", p: 4 }}
              spacing={2}
            >
              <Typography level="h3">
                {agentConversations.length
                  ? "Select an agent"
                  : canShowAgentActionMenu(workspaceRole)
                    ? "New Agent"
                    : "No agents available"}
              </Typography>
              <Typography>
                {agentConversations.length
                  ? "Choose an agent from the strip to continue."
                  : canShowAgentActionMenu(workspaceRole)
                    ? "Create your first agent to start working in this workspace."
                    : "There are no agents available in this workspace."}
              </Typography>
              {!agentConversations.length &&
                canShowAgentActionMenu(workspaceRole) && (
                  <Button
                    onClick={onNewConversation}
                    disabled={conversationsLoading}
                  >
                    New Agent
                  </Button>
                )}
            </Stack>
          ) : historyLoading ? (
            <Box
              sx={{
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              <CircularProgress
                data-testid="conversation-history-loading"
                size="md"
              />
            </Box>
          ) : messages.length === 0 && !hasOlderMessages ? (
            <Box
              sx={{
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              {historyLoadFailed ? (
                <Stack spacing={1.5} alignItems="center">
                  <Typography
                    data-testid="conversation-load-failed"
                    level="body-md"
                    sx={{
                      color: "text.tertiary",
                      textAlign: "center",
                      maxWidth: 500,
                    }}
                  >
                    This conversation could not be loaded. Reload the page to
                    try again.
                  </Typography>
                  {onRetryHistoryLoad && (
                    <Button
                      size="sm"
                      variant="soft"
                      onClick={onRetryHistoryLoad}
                    >
                      Retry
                    </Button>
                  )}
                </Stack>
              ) : (
                /* Only show welcome content once data is loaded (props are defined) */
                welcomeTitle !== undefined && (
                  <>
                    <MaterialIcon
                      name={welcomeIconName}
                      sx={{
                        fontSize: 64,
                        color: "var(--joy-palette-neutral-300)",
                        marginBottom: 16,
                      }}
                    />
                    <Typography
                      level="h3"
                      sx={{ color: "text.secondary", mb: 1 }}
                    >
                      {welcomeTitle}
                    </Typography>
                    <Typography
                      level="body-md"
                      sx={{
                        color: "text.tertiary",
                        textAlign: "center",
                        maxWidth: 500,
                      }}
                    >
                      {welcomeDescription}
                    </Typography>
                  </>
                )
              )}
            </Box>
          ) : (
            <MessageList
              messages={messages}
              toolComposingLabel={toolComposingLabel}
              hasOlderMessages={hasOlderMessages}
              onLoadOlderMessages={onLoadOlderMessages}
              olderMessagesLoading={olderMessagesLoading}
              olderMessagesLoadFailed={olderMessagesLoadFailed}
              currentUserId={currentUser?.id}
              contentRenderers={contentRenderers}
              onCitationClick={onCitationClick}
              messageExtensions={messageExtensions}
              messageIconFor={messageIconFor}
              toolExtensions={toolExtensions}
              chatEndRef={chatEndRef}
              toolExecutionSignature={toolExecutionSignature}
              editingMessageId={editingMessage?.id}
              skillAttributionEnabled={skillAttributionEnabled}
              skillAttributionsByTurnId={skillAttributionsByTurnId}
            />
          )}
        </Box>

        {/* Input area: agent-mode has no plain-chat destination. */}
        {shouldShowComposer && (
          <Box
            ref={composerRef}
            data-testid="chat-composer-container"
            sx={{
              px: 2,
              pt: 1.5,
              pb: "calc(16px + env(safe-area-inset-bottom))",
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.surface",
              position: "fixed",
              bottom: 0,
              left: "var(--workspace-sidebar-width, 0px)",
              right: 0,
              zIndex: 5,
            }}
          >
            <Box sx={{ maxWidth: 900, mx: "auto" }}>
              {followUpSuggestions.length > 0 &&
                !followUpSuggestionsBlocked && (
                  <Box sx={{ mb: 1 }}>
                    <FollowUpSuggestionChips
                      suggestions={followUpSuggestions}
                      onSelect={handleFollowUpSuggestionSelect}
                    />
                  </Box>
                )}

              {backgroundJobs.some(
                (job) => job.status === "pending" || job.status === "running",
              ) && (
                <Box sx={{ mb: 1 }}>
                  <JobProgressIndicator
                    jobs={backgroundJobs}
                    onDismiss={handleDismissJob}
                    showStatuses={["pending", "running"]}
                  />
                </Box>
              )}

              {fileError &&
                fileError !== "IMAGE_INPUT_UNSUPPORTED" &&
                fileError !== "IMAGE_INPUT_DISABLED" && (
                  <Box sx={{ mb: 1 }}>
                    <Alert
                      color="warning"
                      variant="soft"
                      endDecorator={
                        <IconButton
                          variant="plain"
                          size="sm"
                          color="warning"
                          onClick={() => setFileError(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      {fileError}
                    </Alert>
                  </Box>
                )}

              {messagesSyncError && (
                <Box sx={{ mb: 1 }}>
                  <Alert
                    color="warning"
                    variant="soft"
                    endDecorator={
                      onDismissMessagesSyncError && (
                        <IconButton
                          variant="plain"
                          size="sm"
                          color="warning"
                          aria-label="Dismiss"
                          onClick={onDismissMessagesSyncError}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    {messagesSyncError}
                  </Alert>
                </Box>
              )}

              {editAttachmentRestoreState === "restoring" && (
                <Box sx={{ mb: 1 }}>
                  <Alert
                    size="sm"
                    color="neutral"
                    variant="soft"
                    startDecorator={<CircularProgress size="sm" />}
                  >
                    Restoring original attachments…
                  </Alert>
                </Box>
              )}

              {gptLiveVoiceFeedback && (
                <Box
                  data-testid="gpt-live-feedback-row"
                  style={{
                    position: "static",
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}
                  sx={{
                    mb: 1,
                  }}
                >
                  <Alert
                    color={gptLiveVoiceFeedback.tone}
                    variant="soft"
                    role={
                      gptLiveVoiceFeedback.tone === "danger"
                        ? "alert"
                        : "status"
                    }
                    style={{ width: "100%", boxSizing: "border-box" }}
                  >
                    {gptLiveVoiceFeedback.message}
                  </Alert>
                </Box>
              )}

              {queueError && (
                <Box sx={{ mb: 1 }}>
                  <Alert color="danger" variant="soft" role="alert">
                    {queueError}
                  </Alert>
                </Box>
              )}

              {queuedMessages.length > 0 && (
                <Stack
                  data-testid="conversation-queue"
                  spacing={0.5}
                  sx={{ mb: 1, width: "100%" }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography level="body-sm">Queued messages</Typography>
                    {onCancelAllQueuedMessages &&
                      queuedMessages.some(
                        (item) =>
                          item.status === "queued" || item.status === "running",
                      ) && (
                        <Button
                          size="sm"
                          variant="plain"
                          onClick={() => void onCancelAllQueuedMessages()}
                        >
                          Cancel all
                        </Button>
                      )}
                  </Stack>
                  {queuedMessages.map((item) => (
                    <Stack
                      key={item.submission_id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      data-testid={`queued-message-${item.submission_id}`}
                    >
                      <Typography level="body-xs" sx={{ flex: 1 }}>
                        {item.status === "failed"
                          ? item.reason === "worker_restarted"
                            ? "Message failed because the worker restarted."
                            : "Message failed."
                          : item.status === "running"
                            ? "Message is running."
                            : `Queued message ${item.position}.`}
                      </Typography>
                      {item.status !== "failed" && onCancelQueuedMessage && (
                        <Button
                          size="sm"
                          variant="plain"
                          onClick={() => void onCancelQueuedMessage(item)}
                          aria-label={`Cancel message ${item.position}`}
                        >
                          Cancel
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* The picker renders its button, chips and errors as one unit. The
                display:contents rules make the button a flex item of this row
                (left of the input) and let the chips/errors wrap onto their own
                line above it. */}
              <Stack
                data-testid="chat-composer-input-row"
                direction="row"
                alignItems="flex-end"
                sx={{
                  gap: 1,
                  flexWrap: "wrap",
                  "& .mmagent-attachments": { display: "contents" },
                  "& .mmagent-attachments__chips": {
                    order: -1,
                    flex: "0 0 100%",
                  },
                  "& .mmagent-attachments__chips:empty": { display: "none" },
                  "& .mmagent-attachments__error": {
                    order: -1,
                    flex: "0 0 100%",
                  },
                }}
              >
                {(onSendMessageWithFiles ||
                  (imageAttachmentsEnabled && uploadImage)) && (
                  <AttachmentPicker
                    attachments={attachments}
                    onChange={handleAttachmentsChange}
                    uploadImage={
                      imageAttachmentsEnabled ? uploadImage : undefined
                    }
                    fileHandlers={
                      onSendMessageWithFiles
                        ? Object.fromEntries(
                            ALLOWED_EXTENSIONS.filter(
                              (extension) =>
                                !(
                                  imageAttachmentsEnabled &&
                                  uploadImage &&
                                  /^(\.png|\.jpe?g|\.webp)$/.test(extension)
                                ),
                            ).map((extension) => [
                              extension,
                              async (file: File) => file,
                            ]),
                          )
                        : undefined
                    }
                    pasteTargetRef={textareaRef}
                    refusal={
                      serverImageRefusal ||
                      (fileError === "IMAGE_INPUT_UNSUPPORTED" ||
                      fileError === "IMAGE_INPUT_DISABLED"
                        ? fileError
                        : null)
                    }
                    disabled={
                      isUploading ||
                      chatDisabled ||
                      conversationSwitching ||
                      agentCreationPending
                    }
                  />
                )}
                {voiceInputEnabled && workspaceId && (
                  <VoiceInputButton
                    workspaceId={workspaceId}
                    disabled={
                      isLoading ||
                      isUploading ||
                      chatDisabled ||
                      conversationSwitching ||
                      agentCreationPending
                    }
                    onTranscriptUpdate={setInputValue}
                    onAutoSubmit={(transcript) => {
                      setInputValue(transcript);
                      // Defer to next tick so the input value is committed before send.
                      window.setTimeout(() => {
                        handleSend();
                      }, 0);
                    }}
                    userId={currentUser?.id ?? null}
                  />
                )}

                {gptLiveVoiceControl && (
                  <Box
                    data-testid="chat-composer-gpt-live-slot"
                    sx={{ flex: "0 0 112px", width: 112, minWidth: 112 }}
                  >
                    {gptLiveVoiceControl}
                  </Box>
                )}

                <Textarea
                  data-testid="chat-composer-textarea"
                  slotProps={{
                    textarea: {
                      ref: textareaRef,
                    },
                  }}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    chatDisabled
                      ? chatDisabledMessage
                      : isLoading || isUploading
                        ? "Thinking..."
                        : (inputPlaceholder ?? "")
                  }
                  disabled={
                    isUploading ||
                    chatDisabled ||
                    conversationSwitching ||
                    agentCreationPending
                  }
                  autoFocus
                  minRows={1}
                  maxRows={12}
                  sx={{ flex: 1 }}
                />
                <Button
                  onClick={handleSend}
                  aria-label={isLoading ? "Queue message" : "Send message"}
                  disabled={
                    (!inputValue.trim() && images.length === 0) ||
                    isUploading ||
                    chatDisabled ||
                    conversationSwitching ||
                    editAttachmentRestoreState === "restoring"
                  }
                >
                  <SendIcon />
                </Button>
                {isLoading && onCancelStream && (
                  <Tooltip
                    title={isStopping ? "Cancelling" : "Stop generating"}
                  >
                    <Button
                      onClick={onCancelStream}
                      disabled={isStopping}
                      aria-label={isStopping ? "Cancelling" : "Stop generating"}
                      color="danger"
                      variant="soft"
                    >
                      {isStopping ? "Cancelling" : <StopIcon />}
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Box>
        )}

        {/* Conversation history drawer. Agent mode uses the inventory bar as navigation. */}
        {workspaceMode !== "agent" && (
          <Drawer
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            anchor="left"
            size="sm"
          >
            <Box sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography level="title-lg">
                  {modeLabels.drawerTitle}
                </Typography>
                <IconButton
                  variant="plain"
                  color="neutral"
                  onClick={() => setHistoryOpen(false)}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
            <Box sx={{ px: 2, pb: 2 }}>
              <Input
                startDecorator={<SearchIcon />}
                placeholder={modeLabels.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                size="sm"
              />
            </Box>
            <Divider />
            <List sx={{ py: 0 }}>
              <ListItem>
                <ListItemButton
                  onClick={onNewConversation}
                  disabled={conversationSwitching || agentCreationPending}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  <ListItemContent>
                    <Typography level="body-sm">
                      {modeLabels.drawerNewItem}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
              <Divider />
              {conversationsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size="sm" />
                </Box>
              ) : conversations.length === 0 ? (
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                      {modeLabels.emptyList}
                    </Typography>
                  </ListItemContent>
                </ListItem>
              ) : (
                <>
                  {conversations.map((convo) => {
                    const isRenamingThis = renamingConvoId === convo.id;
                    const canRename =
                      !!onRenameConversation &&
                      convo.userRole !== "collaborator";
                    const showRowActions =
                      !isRenamingThis &&
                      hoveredConvoId === convo.id &&
                      convo.userRole !== "collaborator";
                    return (
                      <ListItem
                        key={convo.id}
                        onMouseEnter={() => setHoveredConvoId(convo.id)}
                        onMouseLeave={() => setHoveredConvoId(null)}
                        endAction={
                          showRowActions ? (
                            <Stack direction="row" spacing={0.25}>
                              {canRename && (
                                <IconButton
                                  aria-label="Rename"
                                  size="sm"
                                  variant="plain"
                                  color="neutral"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartRenameConvo(
                                      convo.id,
                                      convo.title,
                                    );
                                  }}
                                  data-testid={`ida-conversation-rename-button-${convo.id}`}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              )}
                              {onDeleteConversation && (
                                <IconButton
                                  aria-label="Delete"
                                  size="sm"
                                  color="danger"
                                  variant="plain"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirmId(convo.id);
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          ) : undefined
                        }
                      >
                        {isRenamingThis ? (
                          // Inline-rename row: don't make it clickable as a
                          // conversation-load while the user is editing the
                          // title.
                          <ListItemContent sx={{ px: 1, py: 0.5 }}>
                            <Input
                              size="sm"
                              value={renameDraft}
                              autoFocus
                              disabled={renameSubmitting}
                              onChange={(e) => setRenameDraft(e.target.value)}
                              onBlur={() => {
                                void handleSubmitRenameConvo(convo.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  void handleSubmitRenameConvo(convo.id);
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  handleCancelRenameConvo();
                                }
                              }}
                              slotProps={{ input: { maxLength: 255 } }}
                              placeholder="Press Enter to save, Esc to cancel"
                              data-testid={`ida-conversation-rename-input-${convo.id}`}
                            />
                          </ListItemContent>
                        ) : (
                          <ListItemButton
                            selected={convo.id === currentConversationId}
                            onClick={() => {
                              onLoadConversation(convo.id);
                              setHistoryOpen(false);
                            }}
                          >
                            <ListItemContent>
                              <Stack spacing={0.25}>
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                >
                                  {collaborationEnabled &&
                                    convo.userRole === "collaborator" && (
                                      <GroupOutlinedIcon
                                        fontSize="small"
                                        sx={{ color: "text.tertiary" }}
                                        data-testid={`shared-conversation-icon-${convo.id}`}
                                      />
                                    )}
                                  <Typography level="body-sm">
                                    {convo.title ||
                                      `${modeLabels.rowFallback} ${convo.id}`}
                                  </Typography>
                                </Stack>
                                {collaborationEnabled &&
                                  convo.userRole === "collaborator" && (
                                    <Typography
                                      level="body-xs"
                                      sx={{ color: "text.tertiary" }}
                                    >
                                      {buildSharedConversationLabel({
                                        collaborators:
                                          conversationCollaboratorsById[
                                            convo.id
                                          ] || [],
                                        currentUserId: currentUser?.id,
                                      })}
                                    </Typography>
                                  )}
                              </Stack>
                              <Typography
                                level="body-xs"
                                sx={{ color: "text.tertiary" }}
                              >
                                {new Date(convo.createdAt).toLocaleDateString()}
                              </Typography>
                            </ListItemContent>
                          </ListItemButton>
                        )}
                      </ListItem>
                    );
                  })}
                  {hasMoreConversations && (
                    <ListItem>
                      <ListItemButton onClick={onLoadMoreConversations}>
                        <ListItemContent>
                          <Typography
                            level="body-sm"
                            sx={{ color: "primary.500" }}
                          >
                            Load older...
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}
                </>
              )}
            </List>
          </Drawer>
        )}

        <CollaboratorManagementModal
          open={collaboratorModalOpen}
          onClose={() => setCollaboratorModalOpen(false)}
          workspaceUsers={workspaceUsers}
          collaborators={currentConversationCollaborators}
          loading={conversationCollaboratorsLoading}
          saving={collaborationSaving}
          error={conversationCollaboratorsError}
          onAdd={handleAddCollaborators}
          onRemove={handleRemoveCollaborator}
        />

        <Modal
          open={personaConversation !== null}
          onClose={() => setPersonaConversation(null)}
        >
          <ModalDialog variant="outlined" sx={{ width: "min(560px, 92vw)" }}>
            <DialogTitle>Edit agent persona</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  Agent persona changes update this agent's future system
                  prompt.
                </Typography>
                <FormControl>
                  <FormLabel>Agent name</FormLabel>
                  <Input
                    value={personaNameDraft}
                    onChange={(event) =>
                      setPersonaNameDraft(event.target.value)
                    }
                    placeholder="Agent name"
                    aria-label="Agent name"
                    slotProps={{ input: { maxLength: 120 } }}
                    data-testid="agent-persona-name-input"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Persona instructions</FormLabel>
                  <Textarea
                    value={personaPromptDraft}
                    onChange={(event) =>
                      setPersonaPromptDraft(event.target.value)
                    }
                    minRows={5}
                    placeholder="Operating style, role, or constraints"
                    aria-label="Agent persona instructions"
                    data-testid="agent-persona-prompt-input"
                  />
                </FormControl>
                {personaError && (
                  <Typography role="alert" level="body-sm" color="danger">
                    {personaError}
                  </Typography>
                )}
                <Box>
                  <Typography level="title-sm">Reusable personas</Typography>
                  <Typography
                    level="body-xs"
                    sx={{ color: "text.tertiary", mb: 0.75 }}
                  >
                    Selecting a card copies its text into this agent. Later
                    library edits only apply when you reload a card.
                  </Typography>
                  {personaLibraryLoading ? (
                    <CircularProgress size="sm" />
                  ) : (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ overflowX: "auto", pb: 0.5 }}
                    >
                      <Box sx={{ minWidth: 210 }}>
                        <Typography
                          level="body-xs"
                          sx={{ fontWeight: "lg", mb: 0.5 }}
                        >
                          Your personas
                        </Typography>
                        {personaLibrary.own.map((persona) => (
                          <Box key={persona.id} sx={{ mb: 0.75 }}>
                            <Button
                              variant={
                                persona.is_update_available ? "solid" : "soft"
                              }
                              color={
                                persona.is_update_available
                                  ? "warning"
                                  : "neutral"
                              }
                              size="sm"
                              loading={personaSubmitting}
                              onClick={() =>
                                void handleApplyLibraryPersona(persona.id)
                              }
                              sx={{
                                display: "flex",
                                width: "100%",
                                justifyContent: "space-between",
                                textAlign: "left",
                              }}
                            >
                              <span>
                                {persona.name} v{persona.version}
                              </span>
                              {persona.is_update_available
                                ? "Reload update"
                                : "Load"}
                            </Button>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              sx={{ mt: 0.25 }}
                            >
                              <Checkbox
                                size="sm"
                                label="Share"
                                checked={persona.is_shared}
                                disabled={personaSubmitting}
                                onChange={(event) =>
                                  void handleTogglePersonaSharing(
                                    persona,
                                    event.target.checked,
                                  )
                                }
                                slotProps={{
                                  input: {
                                    "aria-label": `Share ${persona.name} with organisation`,
                                  },
                                }}
                              />
                              <Button
                                size="sm"
                                variant="plain"
                                onClick={() => handleBeginPersonaEdit(persona)}
                                disabled={personaSubmitting}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={() =>
                                  void handleDeleteLibraryPersona(persona)
                                }
                                disabled={personaSubmitting}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ minWidth: 190 }}>
                        <Typography
                          level="body-xs"
                          sx={{ fontWeight: "lg", mb: 0.5 }}
                        >
                          Shared with you
                        </Typography>
                        {personaLibrary.shared.map((persona) => (
                          <Button
                            key={persona.id}
                            variant={
                              persona.is_update_available ? "solid" : "soft"
                            }
                            color={
                              persona.is_update_available
                                ? "warning"
                                : "neutral"
                            }
                            size="sm"
                            loading={personaSubmitting}
                            onClick={() =>
                              void handleApplyLibraryPersona(persona.id)
                            }
                            sx={{
                              display: "flex",
                              width: "100%",
                              justifyContent: "space-between",
                              mb: 0.5,
                              textAlign: "left",
                            }}
                          >
                            <span>
                              {persona.name} v{persona.version}
                            </span>
                            {persona.is_update_available
                              ? "Reload update"
                              : "Load"}
                          </Button>
                        ))}
                      </Box>
                    </Stack>
                  )}
                  {personaLibraryError && (
                    <Alert color="danger" size="sm" sx={{ mt: 0.5 }}>
                      {personaLibraryError}
                    </Alert>
                  )}
                </Box>
                {personaEditing && (
                  <Box
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "divider",
                      pt: 1,
                    }}
                  >
                    <Typography level="title-sm">
                      Edit reusable persona
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                      <Input
                        value={personaEditName}
                        onChange={(event) =>
                          setPersonaEditName(event.target.value)
                        }
                        placeholder="Persona name"
                        aria-label="Reusable persona name"
                        slotProps={{ input: { maxLength: 120 } }}
                      />
                      <Textarea
                        value={personaEditBody}
                        onChange={(event) =>
                          setPersonaEditBody(event.target.value)
                        }
                        minRows={3}
                        aria-label="Reusable persona instructions"
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox
                          label="Share edited persona with organisation"
                          checked={personaEditShared}
                          onChange={(event) =>
                            setPersonaEditShared(event.target.checked)
                          }
                        />
                        <Button
                          size="sm"
                          variant="outlined"
                          loading={personaSubmitting}
                          onClick={() => void handleUpdateLibraryPersona()}
                          disabled={
                            !personaEditName.trim() || !personaEditBody.trim()
                          }
                        >
                          Save changes
                        </Button>
                        <Button
                          size="sm"
                          variant="plain"
                          onClick={() => setPersonaEditing(null)}
                          disabled={personaSubmitting}
                        >
                          Cancel edit
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                )}
                <Box
                  sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1 }}
                >
                  <Typography level="title-sm">
                    Save as reusable persona
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    <Input
                      value={personaLibraryName}
                      onChange={(event) =>
                        setPersonaLibraryName(event.target.value)
                      }
                      placeholder="Persona name"
                      slotProps={{ input: { maxLength: 120 } }}
                      sx={{ flex: 1 }}
                    />
                    <Checkbox
                      label="Share with organisation"
                      checked={personaLibraryShared}
                      onChange={(event) =>
                        setPersonaLibraryShared(event.target.checked)
                      }
                    />
                    <Button
                      size="sm"
                      variant="outlined"
                      loading={personaSubmitting}
                      onClick={() => void handleSaveLibraryPersona()}
                      disabled={
                        !personaLibraryName.trim() || !personaPromptDraft.trim()
                      }
                    >
                      Save
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                variant="solid"
                onClick={() => void handleSubmitPersona()}
                loading={personaSubmitting}
                disabled={!personaNameDraft.trim()}
              >
                Save persona
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setPersonaConversation(null)}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>

        <Modal
          open={Boolean(avatarPickerConversation)}
          onClose={() =>
            !avatarActionSaving && setAvatarPickerConversation(null)
          }
        >
          <ModalDialog aria-labelledby="agent-avatar-picker-title">
            <DialogTitle id="agent-avatar-picker-title">
              Choose agent appearance
            </DialogTitle>
            <DialogContent>
              <Stack spacing={1}>
                <Typography level="body-sm">
                  Choose a cut and colour for this agent.
                </Typography>
                {avatarActionError && (
                  <Alert color="warning">{avatarActionError}</Alert>
                )}
                {avatarAvailabilityError && (
                  <Alert color="warning">{avatarAvailabilityError}</Alert>
                )}
                {avatarReservations &&
                  (!availableAvatarCuts.length ||
                    !availableAvatarColours.length) && (
                    <Alert color="warning">
                      No appearance values are available in this workspace.
                    </Alert>
                  )}
                <FormControl>
                  <FormLabel>Cut</FormLabel>
                  <Select
                    value={avatarPickerCut}
                    disabled={
                      avatarAvailabilityLoading || !availableAvatarCuts.length
                    }
                    onChange={(_, value) =>
                      value && setAvatarPickerCut(value as AgentAvatarCut)
                    }
                  >
                    {availableAvatarCuts.map((cut) => (
                      <Option key={cut} value={cut}>
                        {cut}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Colour</FormLabel>
                  <Select
                    value={avatarPickerColour}
                    disabled={
                      avatarAvailabilityLoading ||
                      !availableAvatarColours.length
                    }
                    onChange={(_, value) =>
                      value && setAvatarPickerColour(value as AgentAvatarColour)
                    }
                  >
                    {availableAvatarColours.map((colour) => (
                      <Option key={colour} value={colour}>
                        {colour}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                {avatarPickerConversation &&
                  avatarPickerCut &&
                  avatarPickerColour && (
                    <AgentGemstone
                      id={avatarPickerConversation.id}
                      cut={avatarPickerCut}
                      colour={avatarPickerColour}
                      size={72}
                      title="Selected agent appearance"
                      testId="agent-avatar-picker-preview"
                    />
                  )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                variant="plain"
                onClick={() => setAvatarPickerConversation(null)}
                disabled={avatarActionSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleAssignAvatar()}
                loading={avatarActionSaving}
                disabled={
                  avatarAvailabilityLoading ||
                  !avatarPickerCut ||
                  !avatarPickerColour ||
                  !availableAvatarCuts.length ||
                  !availableAvatarColours.length
                }
              >
                Assign
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>

        {/* Delete confirmation modal */}
        <Modal
          open={deleteConfirmId !== null}
          onClose={() => {
            setDeleteConfirmId(null);
            setDeleteConversationError(null);
          }}
        >
          <ModalDialog variant="outlined" role="alertdialog">
            <DialogTitle>{modeLabels.deleteTitle}</DialogTitle>
            <DialogContent>
              {modeLabels.deleteBody}
              {deleteConversationError && (
                <Alert color="warning" sx={{ mt: 1 }}>
                  {deleteConversationError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                variant="solid"
                color="danger"
                onClick={async () => {
                  if (!deleteConfirmId || !onDeleteConversation) return;
                  setDeleteConversationError(null);
                  try {
                    await onDeleteConversation(deleteConfirmId);
                    setDeleteConfirmId(null);
                  } catch {
                    setDeleteConversationError(
                      "Delete did not complete. This agent remains visible and can be retried.",
                    );
                  }
                }}
              >
                {deleteConversationError ? "Retry delete" : "Delete"}
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  setDeleteConfirmId(null);
                  setDeleteConversationError(null);
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>

        <Snackbar
          open={Boolean(collaborationNotice)}
          autoHideDuration={2500}
          onClose={() => setCollaborationNotice(null)}
          color="success"
          variant="soft"
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          {collaborationNotice}
        </Snackbar>

        {/* Delete message confirmation modal */}
        <Modal
          open={deleteMessageConfirm !== null}
          onClose={() => setDeleteMessageConfirm(null)}
        >
          <ModalDialog variant="outlined" role="alertdialog">
            <DialogTitle>Delete message?</DialogTitle>
            <DialogContent>
              This will delete this message and all messages after it. This
              action cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button
                variant="solid"
                color="danger"
                onClick={async () => {
                  if (deleteMessageConfirm && onDeleteMessagesFrom) {
                    await onDeleteMessagesFrom(deleteMessageConfirm);
                  }
                  setDeleteMessageConfirm(null);
                }}
              >
                Delete
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setDeleteMessageConfirm(null)}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      </Box>
    </>
  );
};
